import { PaymentConfirmation, corePaymentMethodLabel, chargeStatusLabel, totalLabelFromLines, type ConfirmationLine } from './PaymentConfirmation';
import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { onlyDigits, toE164Phone } from './phone';
import type { BillingPeriodUnit, BuyerPaymentMethod, Money, PublicCheckout, PublicCheckoutBump, PublicCheckoutPlanOption, PublicCheckoutSubscription } from './contract';
import type { CheckoutOrderInput, CheckoutOrderResult, CheckoutOrderSummary, CheckoutVoucher } from './api';
import type { CheckoutCorePaymentMethod } from './api';
import type { CheckoutTrackingIdentifiers, CheckoutUtmAttribution } from './attribution';
import {
  createCheckoutOrder,
  captureCheckoutContact,
  fetchFunnelOffer,
  fetchOrderResult,
  fetchOrderStatus,
  fetchOrderSummary,
  previewCoupon,
  submitFunnelStep,
  useMock,
} from './api';
import { StripeCardPanel } from './StripeCardSection';
import { authenticatePayment } from './stripe-action';
import { loadHostedFunnelPending, saveHostedFunnelPending, clearHostedFunnelPending } from './hosted-funnel-pending';
import { useCheckoutActivity } from './useCheckoutActivity';
import type { StripeCardHandle } from './StripeCardSection';
import { CheckoutErrorAlert } from './CheckoutErrorAlert';
import type { CheckoutErrorDescriptor } from './errorPresentation';
import {
  CheckoutFlowError,
  checkoutBlockCode,
  presentCheckoutOrderError,
  presentLocalizedError,
  presentPaymentError,
  presentProviderError,
  presentSystemError,
  presentValidationError,
} from './errorPresentation';
import { ExpressWalletBand } from './ExpressWalletBand';
import type { WalletConfirmPayload, WalletPaymentMethod } from './ExpressWalletBand';
import { trackAddPaymentInfo, trackPixGenerated } from './tracking-pixels';
import { afterPurchaseTracking, trackConfirmedPurchase } from './purchase-tracking';
import { UpsellStep } from './UpsellStep';
import { SafeImage } from './SafeImage';
import { CheckoutBannerVideo } from './CheckoutBannerVideo';
import { useFeatureFlag } from '../shared/feature-flags/useFeatureFlag';
import { nextFunnelNode, recordFunnelDecision } from './funnel';
import type { FunnelDecision } from './funnel';
import { externalFunnelUrl, thankYouUrl, funnelTrackingParameters } from './external-funnel';
import {
  applyPercentDiscount,
  formatMoney,
  minMoney,
  moneyToMinorUnits,
  periodSuffix,
  subtractMoney,
  sumMoney,
  moneyEquals,
  roundToCurrency,
} from './money';
import {
  BarcodeIcon,
  CardIcon,
  CheckIcon,
  ChevronIcon,
  LockIcon,
  PixIcon,
  ShieldIcon,
} from './icons';
import { LegacyCountdown } from './LegacyCountdown';
import { currentLocale, currentLocaleTag, tr, useCurrentLocale } from '../i18n';
import { useMediaQuery } from './useMediaQuery';

const ORDER_POLL_INTERVAL_MS = 4000;
const DEFAULT_ORDER_POLL_TIMEOUT_MS = 5 * 60 * 1000;
const PIX_ORDER_POLL_TIMEOUT_MS = 24 * 60 * 60 * 1000;

type PaymentMethod = BuyerPaymentMethod;
type PurchaseMethod = PaymentMethod | 'apple_pay' | 'google_pay';

type AppliedCoupon = { amountAfterDiscount?: Money; pricingToken?: string | undefined; offerId?: string | undefined } & (
  | { code: string; type: 'percent'; percent: number }
  | { code: string; type: 'fixed'; amount: Money });

interface CheckoutAttemptSnapshot {
  readonly checkoutId: string;
  readonly orderInput: CheckoutOrderInput;
  readonly purchaseMethod: PurchaseMethod;
  readonly confirmAction?: (clientSecret: string) => Promise<{ ok: true } | { error: CheckoutErrorDescriptor }>;
  readonly failWalletSheet?: boolean;
}


const fieldOrder = ['name', 'email', 'phone', 'document'] as const;
type CheckoutFieldKey = (typeof fieldOrder)[number];

function checkoutFieldMeta(key: CheckoutFieldKey, isBrazil: boolean) {
  if (key === 'name') return { label: tr('field.name'), type: 'text', placeholder: tr('field.namePlaceholder'), autoComplete: 'name', inputMode: 'text' as const };
  if (key === 'email') return { label: tr('field.email'), type: 'email', placeholder: tr('field.emailPlaceholder'), autoComplete: 'email', inputMode: 'email' as const };
  if (key === 'phone' && !isBrazil) return { label: tr('field.internationalPhone'), type: 'tel', placeholder: tr('field.internationalPhonePlaceholder'), autoComplete: 'tel', inputMode: 'tel' as const };
  if (key === 'phone') return { label: tr('field.phone'), type: 'tel', placeholder: tr('field.phonePlaceholder'), autoComplete: 'tel', inputMode: 'tel' as const };
  return { label: tr('field.document'), type: 'text', placeholder: tr('field.documentPlaceholder'), autoComplete: 'off', inputMode: 'numeric' as const };
}

function coreMethod(method: BuyerPaymentMethod): 'PIX' | 'CREDIT_CARD' | 'BOLETO' {
  return method === 'card' ? 'CREDIT_CARD' : method === 'boleto' ? 'BOLETO' : 'PIX';
}

function walletPurchaseMethod(method: WalletPaymentMethod): PurchaseMethod {
  return method === 'APPLE_PAY' ? 'apple_pay' : 'google_pay';
}

function purchaseMethodForFunnel(method: PurchaseMethod): BuyerPaymentMethod {
  if (method === 'pix' || method === 'boleto') {
    return method;
  }
  return 'card';
}

function requiredCheckoutFields(fields: PublicCheckout['presentation']['fields']) {
  return { ...fields, email: true };
}

function piiConsentTargetLabel(fields: Record<CheckoutFieldKey, boolean>): string | null {
  const hasEmail = fields.email;
  const hasPhone = fields.phone;
  if (hasEmail && hasPhone) return tr('pii.emailPhone');
  if (hasEmail) return tr('pii.email');
  if (hasPhone) return tr('pii.phone');
  return null;
}

export function pollTimeoutMsForVoucher(voucher: CheckoutVoucher | null | undefined): number {
  if (!voucher?.pixCopyPaste && !voucher?.pixQrCodeBase64) {
    return DEFAULT_ORDER_POLL_TIMEOUT_MS;
  }
  if (voucher.expiresAt !== undefined) {
    const expiresInMs = Date.parse(voucher.expiresAt) - Date.now();
    if (Number.isFinite(expiresInMs) && expiresInMs > 0) {
      return Math.max(ORDER_POLL_INTERVAL_MS, expiresInMs);
    }
  }
  return PIX_ORDER_POLL_TIMEOUT_MS;
}

function failedPaymentError(
  method: PurchaseMethod,
  declineCode: string | undefined,
  subscription = false,
): CheckoutErrorDescriptor {
  if (method === 'card' || method === 'apple_pay' || method === 'google_pay') {
    return presentPaymentError({
      ...(declineCode !== undefined ? { declineCode } : {}),
      ...(subscription ? { subscription } : {}),
    });
  }
  return presentLocalizedError({ key: 'decline.nonCard', kind: 'payment' });
}

function formatCardExpiryInput(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
}

function formatCardNumberInput(value: string): string {
  return value
    .replace(/\D/g, '')
    .slice(0, 19)
    .replace(/(.{4})/g, '$1 ')
    .trim();
}

function cardBrandLabel(value: string): string | null {
  const digits = value.replace(/\D/g, '');
  if (/^4/.test(digits)) return 'Visa';
  const firstTwo = Number(digits.slice(0, 2));
  const firstFour = Number(digits.slice(0, 4));
  if ((firstTwo >= 51 && firstTwo <= 55) || (firstFour >= 2221 && firstFour <= 2720)) {
    return 'Mastercard';
  }
  return null;
}

function formatCpfInput(value: string): string {
  const digits = onlyDigits(value).slice(0, 11);
  return digits
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1-$2');
}

function formatPhoneInput(value: string): string {
  const digits = onlyDigits(value).slice(0, 11);
  if (digits.length <= 10) {
    return digits.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2');
  }
  return digits.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2');
}

function formatCheckoutFieldInput(key: CheckoutFieldKey, value: string, isBrazil: boolean): string {
  if (key === 'document') return formatCpfInput(value);
  if (key === 'phone') {
    return isBrazil ? formatPhoneInput(value) : value.replace(/[^\d+()\-\s]/g, '').slice(0, 24);
  }
  return value;
}

function zeroMoney(currency: string): Money {
  return { amount: '0.000', currency };
}

function isValidEmail(value: string): boolean {
  return /^(?!\.)(?!.*\.\.)([A-Z0-9_'+\-.]*)[A-Z0-9_+-]@([A-Z0-9][A-Z0-9\-]*\.)+[A-Z]{2,}$/i.test(value);
}

function checkoutContactSignature(sessionId: string, email: string, name?: string, phone?: string): string {
  return `${sessionId}\n${email.trim().toLowerCase()}\n${name?.trim() ?? ''}\n${phone?.trim() ?? ''}`;
}

function isValidCpf(value: string): boolean {
  const digits = onlyDigits(value);
  if (digits.length !== 11 || /^(\d)\1{10}$/.test(digits)) {
    return false;
  }
  const numbers = digits.split('').map((char) => Number(char));
  const firstCheck =
    numbers
      .slice(0, 9)
      .reduce((sum, number, index) => sum + number * (10 - index), 0) % 11;
  const firstDigit = firstCheck < 2 ? 0 : 11 - firstCheck;
  if (numbers[9] !== firstDigit) {
    return false;
  }
  const secondCheck =
    numbers
      .slice(0, 10)
      .reduce((sum, number, index) => sum + number * (11 - index), 0) % 11;
  const secondDigit = secondCheck < 2 ? 0 : 11 - secondCheck;
  return numbers[10] === secondDigit;
}

const PERIOD_DAYS: Record<BillingPeriodUnit, number> = { MINUTE: 1 / 1_440, DAY: 1, WEEK: 7, MONTH: 30, YEAR: 365 };

function planIntervalDays(plan: PublicCheckoutPlanOption): number {
  return PERIOD_DAYS[plan.billingPeriodUnit] * plan.billingPeriodCount;
}

function planOptionLabel(plan: PublicCheckoutPlanOption): string {
  if (plan.name.length > 0) {
    return plan.name;
  }
  const count = plan.billingPeriodCount;
  const unitKey = {
    MINUTE: count > 1 ? 'period.minute.other' : 'period.minute.one',
    DAY: count > 1 ? 'period.day.other' : 'period.day.one',
    WEEK: count > 1 ? 'period.week.other' : 'period.week.one',
    MONTH: count > 1 ? 'period.month.other' : 'period.month.one',
    YEAR: count > 1 ? 'period.year.other' : 'period.year.one',
  }[plan.billingPeriodUnit] as Parameters<typeof tr>[0];
  return count > 1 ? tr('period.every', { count, unit: tr(unitKey) }).trim() : tr('period.for', { unit: tr(unitKey) });
}

function makeSaleId(): string {
  return globalThis.crypto?.randomUUID?.() ?? fallbackSaleUuid();
}

function fallbackSaleUuid(): string {
  const bytes = new Uint8Array(16);
  globalThis.crypto?.getRandomValues?.(bytes);
  if (bytes.every((byte) => byte === 0)) {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function checkoutAttemptSaleIdKey(checkoutSessionId: string): string {
  return `vendepay:checkout-attempt:${checkoutSessionId}`;
}

function loadCheckoutAttemptSaleId(checkoutSessionId: string | undefined): string | null {
  if (checkoutSessionId === undefined) {
    return null;
  }
  try {
    const stored = window.sessionStorage.getItem(checkoutAttemptSaleIdKey(checkoutSessionId));
    return stored !== null && stored.length > 0 && stored.length <= 200 ? stored : null;
  } catch {
    return null;
  }
}

function persistCheckoutAttemptSaleId(checkoutSessionId: string | undefined, saleId: string): void {
  if (checkoutSessionId === undefined) {
    return;
  }
  try {
    window.sessionStorage.setItem(checkoutAttemptSaleIdKey(checkoutSessionId), saleId);
  } catch {
  }
}

function forgetCheckoutAttemptSaleId(checkoutSessionId: string | undefined): void {
  if (checkoutSessionId === undefined) {
    return;
  }
  try {
    window.sessionStorage.removeItem(checkoutAttemptSaleIdKey(checkoutSessionId));
  } catch {
  }
}

function paymentMethodLabel(method: PurchaseMethod): string {
  if (method === 'pix') {
    return tr('payment.pix');
  }
  if (method === 'boleto') {
    return tr('payment.boleto');
  }
  if (method === 'apple_pay') {
    return tr('payment.applePay');
  }
  if (method === 'google_pay') {
    return tr('payment.googlePay');
  }
  return tr('payment.card');
}


function subscriptionRenewalLabel(subscription: PublicCheckoutSubscription | null | undefined): string | undefined {
  if (!subscription) return undefined;
  return `${formatMoney(subscription.value)}${periodSuffix(subscription.billingPeriodUnit, subscription.billingPeriodCount)}`;
}

interface RecurringChargeSummaryLine {
  amountLabel: string;
  periodLabel: string;
}

function recurringChargeSummary(
  charges: readonly Pick<
    PublicCheckoutSubscription,
    'value' | 'billingPeriodUnit' | 'billingPeriodCount'
  >[],
): RecurringChargeSummaryLine[] {
  if (charges.length === 0) return [];
  const byPeriod = new Map<string, Money[]>();
  const periodByKey = new Map<string, Pick<PublicCheckoutSubscription, 'billingPeriodUnit' | 'billingPeriodCount'>>();
  for (const charge of charges) {
    const key = `${charge.value.currency}:${charge.billingPeriodUnit}:${charge.billingPeriodCount}`;
    byPeriod.set(key, [...(byPeriod.get(key) ?? []), charge.value]);
    periodByKey.set(key, charge);
  }
  try {
    return [...byPeriod.entries()].flatMap(([key, amounts]) => {
      const period = periodByKey.get(key)!;
      const total = sumMoney(amounts);
      return total === null
        ? []
        : [{
            amountLabel: formatMoney(total),
            periodLabel: periodSuffix(period.billingPeriodUnit, period.billingPeriodCount),
          }];
    });
  } catch {
    return [];
  }
}

function recurringChargeSummaryLabel(lines: readonly RecurringChargeSummaryLine[]): string | null {
  if (lines.length === 0) return null;
  return lines.map(({ amountLabel, periodLabel }) => `${amountLabel}${periodLabel}`).join(' + ');
}

function bumpIsFreeTrial(item: PublicCheckoutBump): boolean {
  return item.subscription !== undefined && item.subscription.trialDays > 0 && item.subscription.trialType === 'WITH_PAYMENT';
}

function bumpTodayAmount(item: PublicCheckoutBump): Money {
  if (!item.subscription) return item.price;
  if (bumpIsFreeTrial(item)) return zeroMoney(item.subscription.value.currency);
  if (item.subscription.trialDays > 0 && item.subscription.trialPrice) return item.subscription.trialPrice;
  return item.price;
}

function bumpOfferLabel(item: PublicCheckoutBump): string {
  const renewal = subscriptionRenewalLabel(item.subscription);
  if (!item.subscription || renewal === undefined) {
    return tr('subscription.bumpOneTime', { title: item.title, value: formatMoney(item.price) });
  }
  if (bumpIsFreeTrial(item)) {
    return tr('subscription.bumpTrial', { title: item.title, days: item.subscription.trialDays, renewal });
  }
  if (item.subscription.trialDays > 0 && item.subscription.trialPrice) {
    return tr('subscription.bumpPaidTrial', { title: item.title, trial: formatMoney(item.subscription.trialPrice), days: item.subscription.trialDays, renewal });
  }
  return tr('subscription.bumpRecurring', { title: item.title, value: renewal });
}

function OrderBumpList({
  items,
  selectedIds,
  onToggle,
  placement,
  disabled = false,
}: {
  items: PublicCheckoutBump[];
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  placement: 'inline' | 'sidebar';
  disabled?: boolean;
}) {
  if (items.length === 0) return null;
  return (
    <div className={`vp-bumps vp-bumps--${placement}`}>
      {items.map((item) => {
        const added = selectedIds.has(item.id);
        return (
          <button
            key={item.id}
            type="button"
            className={`vp-bump ${added ? 'is-added' : ''}`}
            onClick={() => onToggle(item.id)}
            disabled={disabled}
            aria-pressed={added}
          >
            <span className="vp-bump-check" aria-hidden="true">
              {added ? <CheckIcon width={16} height={16} /> : null}
            </span>
            {item.imageUrl && <SafeImage src={item.imageUrl} alt="" className="vp-bump-img" />}
            <span className="vp-bump-body">
              <strong>{bumpOfferLabel(item)}</strong>
              <span className="vp-faint vp-small">{item.description}</span>
              {item.subscription && (
                <span className="vp-faint vp-small">
                  {tr('subscription.bumpTerms')}
                </span>
              )}
            </span>
            {item.callToAction && <span className="vp-bump-cta">{item.callToAction}</span>}
          </button>
        );
      })}
    </div>
  );
}

export function CheckoutView({
  checkout,
  preview = false,
  affiliateRef,
  utm,
  trackingIdentifiers,
  recuperaaiCartId,
  refreshCheckout,
  ensureCheckoutSession,
  onActivity,
  onBusyChange,
  skin,
}: {
  checkout: PublicCheckout;
  preview?: boolean;
  affiliateRef?: string | undefined;
  utm?: CheckoutUtmAttribution | undefined;
  trackingIdentifiers?: CheckoutTrackingIdentifiers | undefined;
  recuperaaiCartId?: string | undefined;
  refreshCheckout?: (() => Promise<PublicCheckout>) | undefined;
  ensureCheckoutSession?: ((force?: boolean) => Promise<string | undefined>) | undefined;
  onActivity?: (() => void | Promise<unknown>) | undefined;
  onBusyChange?: ((busy: boolean) => void) | undefined;
  skin?: 'new' | 'legacy';
}) {
  useCurrentLocale();
  const resolvedSkin = skin ?? (checkout.experiment?.variant === 'legacy' ? 'legacy' : 'new');
  const quoteMode = ensureCheckoutSession !== undefined;
  const { presentation, product, offer, orderBumps, payment, refundDays, acceptsCoupon } = checkout;
  const imageBannerEnabled = useFeatureFlag('checkoutImageBanner');
  const videoBannerEnabled = useFeatureFlag('checkoutBannerVideo');
  const showVideoBanner = videoBannerEnabled && Boolean(presentation.bannerVideoUrl);
  const productThumbUrl = imageBannerEnabled ? product.coverImage : presentation.bannerUrl || product.coverImage;
  const hasGuarantee = refundDays > 0;
  const checkoutCountry = checkout.pricing?.country.toUpperCase();
  const isBrazilBuyer = checkoutCountry === undefined || checkoutCountry === 'BR';
  const checkoutFields = {
    ...requiredCheckoutFields(presentation.fields),
    document: presentation.fields.document && isBrazilBuyer,
  };
  const piiTargetLabel = piiConsentTargetLabel(checkoutFields);
  const [refreshedFunnelNodes, setRefreshedFunnelNodes] = useState<Record<string, PublicCheckout['funnel'][number]>>({});
  const funnel = (checkout.funnel ?? []).map(node => refreshedFunnelNodes[node.id] ?? node);
  const subscription = checkout.subscription ?? null;
  const isSubscription = subscription !== null;
  const planOptions: PublicCheckoutPlanOption[] = checkout.plans ?? (subscription ? [{ ...subscription, name: '' }] : []);
  const bumps = presentation.blocks.bump ? orderBumps : [];
  const configuredMethods: PaymentMethod[] = payment.methods.length > 0 ? payment.methods : ['card'];
  const methods: PaymentMethod[] = isSubscription
    ? configuredMethods.includes('card')
      ? ['card']
      : []
    : configuredMethods;
  const showSecurity = presentation.blocks.security;

  const [method, setMethod] = useState<PaymentMethod>(methods[0] ?? 'card');
  const [selectedPlanId, setSelectedPlanId] = useState<string | undefined>(subscription?.planId);
  const [trialStarted, setTrialStarted] = useState(false);
  const [pendingFreeTrial, setPendingFreeTrial] = useState(false);
  const [selectedBumpIds, setSelectedBumpIds] = useState<Set<string>>(new Set());
  const [values, setValues] = useState<Record<string, string>>({});
  const [piiConsent, setPiiConsent] = useState(false);
  const [restoredFunnel] = useState(() => preview ? null : loadHostedFunnelPending(checkout.checkoutId));
  const [phase, setPhase] = useState<
    'idle' | 'processing' | 'done' | 'pending' | 'requires-action' | 'funnel'
  >(restoredFunnel ? 'funnel' : 'idle');
  const [voucher, setVoucher] = useState<CheckoutVoucher | null>(null);
  const [originalSaleId, setOriginalSaleId] = useState<string | null>(restoredFunnel?.originalSaleId ?? null);
  const [funnelHandoffId, setFunnelHandoffId] = useState<string | null>(restoredFunnel?.handoffId ?? null);
  const [funnelDecisions, setFunnelDecisions] = useState<FunnelDecision[]>(restoredFunnel?.decisions ?? []);
  const [funnelBusy, setFunnelBusy] = useState(false);
  const [funnelError, setFunnelError] = useState<CheckoutErrorDescriptor | null>(restoredFunnel && !restoredFunnel.voucher
    ? presentLocalizedError({ key: 'error.offerProcessing', kind: 'payment', action: 'retry' }) : null);
  const [funnelVoucher, setFunnelVoucher] = useState<CheckoutVoucher | null>(restoredFunnel?.voucher ?? null);
  const [funnelPendingSaleId, setFunnelPendingSaleId] = useState<string | null>(restoredFunnel?.saleId ?? null);
  const [funnelPendingNodeId, setFunnelPendingNodeId] = useState<string | null>(restoredFunnel?.nodeId ?? null);
  const [funnelConfirmation, setFunnelConfirmation] = useState<{ title: string; body: string } | null>(null);
  const [orderSummary, setOrderSummary] = useState<CheckoutOrderSummary | null>(null);
  const [orderSummaryLoading, setOrderSummaryLoading] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [couponOpen, setCouponOpen] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<AppliedCoupon | null>(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState<CheckoutErrorDescriptor | null>(null);
  const [orderError, setOrderError] = useState<CheckoutErrorDescriptor | null>(null);
  const [priceBlocked, setPriceBlocked] = useState(false);
  const [sessionEnsureFailed, setSessionEnsureFailed] = useState(false);
  const [fieldError, setFieldError] = useState<CheckoutErrorDescriptor | null>(null);
  const [checkoutToastDismissed, setCheckoutToastDismissed] = useState(false);
  const [cardName, setCardName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [stripeCardMounted, setStripeCardMounted] = useState(false);
  const [pendingSaleId, setPendingSaleId] = useState<string | null>(null);
  const [purchaseMethod, setPurchaseMethod] = useState<PurchaseMethod>(restoredFunnel?.method ?? 'pix');
  const [activeWallet, setActiveWallet] = useState<WalletPaymentMethod | null>(null);
  useEffect(() => { onBusyChange?.(phase !== 'idle' || activeWallet !== null); }, [phase, activeWallet, onBusyChange]);
  const previousPhase = useRef(phase);
  useEffect(() => {
    if (previousPhase.current !== 'idle' && phase === 'idle') setActiveWallet(null);
    previousPhase.current = phase;
  }, [phase]);
  const [walletAvailability, setWalletAvailability] = useState<'probing' | 'available' | 'unavailable'>('probing');
  const [alternativePaymentOpen, setAlternativePaymentOpen] = useState(false);
  const isDesktopLayout = useMediaQuery('(min-width: 960px)');
  const cardRef = useRef<StripeCardHandle>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const documentInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (phase === 'idle' && fieldError?.field === 'email') {
      emailInputRef.current?.focus();
    }
  }, [phase, fieldError?.field]);
  const confirmationTimerRef = useRef<number | null>(null);
  const submittingRef = useRef(false);
  const externalNavigationRef = useRef(false);
  const checkoutAttemptSaleIdRef = useRef<string | null>(
    loadCheckoutAttemptSaleId(checkout.checkoutSessionId),
  );
  const checkoutAttemptStorageSessionIdRef = useRef(checkout.checkoutSessionId);
  if (checkout.checkoutSessionId !== undefined) {
    checkoutAttemptStorageSessionIdRef.current = checkout.checkoutSessionId;
  }
  const checkoutAttemptSnapshotRef = useRef<CheckoutAttemptSnapshot | null>(null);
  useEffect(() => {
    if (checkoutAttemptSnapshotRef.current === null && checkoutAttemptSaleIdRef.current === null) {
      checkoutAttemptSaleIdRef.current = loadCheckoutAttemptSaleId(checkout.checkoutSessionId);
    }
  }, [checkout.checkoutSessionId]);
  const lastCapturedContactRef = useRef<string | null>(null);
  const recordActivity = useCheckoutActivity(
    onActivity,
    !preview && !priceBlocked && phase === 'idle',
  );

  const isMock = useMock(checkout.checkoutId);
  const useStripeCard = !isMock && payment.gateway === 'STRIPE' && payment.publishableKey.length > 0;

  const selectedPlan = planOptions.find((option) => option.planId === selectedPlanId) ?? planOptions[0] ?? subscription;
  const priceSuffix = selectedPlan
    ? periodSuffix(selectedPlan.billingPeriodUnit, selectedPlan.billingPeriodCount)
    : '';
  const isFreeTrial =
    isSubscription && selectedPlan !== null && selectedPlan.trialDays > 0 && selectedPlan.trialType === 'WITH_PAYMENT';
  const firstChargeLabel =
    isFreeTrial && selectedPlan !== null
      ? new Date(Date.now() + selectedPlan.trialDays * 24 * 60 * 60 * 1000).toLocaleDateString(currentLocaleTag())
      : '';
  const planIntervalsDiffer =
    new Set(planOptions.map((option) => `${option.billingPeriodUnit}:${option.billingPeriodCount}`)).size > 1;
  const planHasBenefits = planOptions.some((option) => (option.benefits?.length ?? 0) > 0);
  const bestValuePlanId =
    planOptions.length > 1 && planIntervalsDiffer
      ? planOptions.reduce((best, option) => (planIntervalDays(option) > planIntervalDays(best) ? option : best), planOptions[0]!).planId
      : undefined;

  useEffect(() => {
    return () => {
      if (confirmationTimerRef.current !== null) {
        window.clearTimeout(confirmationTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (phase !== 'pending' || pendingSaleId === null || isMock || preview) {
      return;
    }
    let active = true;
    let interval: number;
    const timeout = window.setTimeout(() => {
      if (!active) return;
      active = false;
      window.clearInterval(interval);
      setOrderError(presentLocalizedError({ key: 'error.confirmationExpired', kind: 'payment', action: 'retry' }));
      setPendingFreeTrial(false);
      setPhase('idle');
    }, pollTimeoutMsForVoucher(voucher));
    interval = window.setInterval(async () => {
      try {
        const order = funnelHandoffId === null
          ? await fetchOrderResult(checkout.checkoutId, pendingSaleId)
          : await fetchOrderResult(checkout.checkoutId, pendingSaleId, funnelHandoffId);
        const status = order.status ?? 'PENDING';
        if (!active) return;
        if (status === 'PAID') {
          window.clearInterval(interval);
          window.clearTimeout(timeout);
          if (pendingFreeTrial) {
            clearCheckoutAttemptSaleId();
            setPendingFreeTrial(false);
            setTrialStarted(true);
            setPhase('done');
            return;
          }
          advanceAfterPurchase(
            pendingSaleId,
            purchaseMethod,
            order.funnelHandoffId ?? funnelHandoffId ?? undefined,
          );
        } else if (status === 'FAILED') {
          window.clearInterval(interval);
          window.clearTimeout(timeout);
          setOrderError(
            pendingFreeTrial
              ? failedPaymentError(purchaseMethod, order.declineCode, true)
              : failedPaymentError(purchaseMethod, order.declineCode),
          );
          clearCheckoutAttemptSaleId();
          setPendingFreeTrial(false);
          setPhase('idle');
        }
      } catch {
      }
    }, ORDER_POLL_INTERVAL_MS);
    return () => {
      active = false;
      window.clearInterval(interval);
      window.clearTimeout(timeout);
    };
  }, [phase, pendingSaleId, pendingFreeTrial, isMock, preview, purchaseMethod, funnelHandoffId, voucher, checkout.checkoutId, checkout.funnel]);

  useEffect(() => {
    if (phase !== 'funnel' || funnelBusy || funnelPendingSaleId === null || funnelPendingNodeId === null || isMock || preview) {
      return;
    }
    let active = true;
    let interval: number;
    const timeout = window.setTimeout(() => {
      if (!active) return;
      active = false;
      window.clearInterval(interval);
      setFunnelVoucher(null);
      setFunnelPendingSaleId(null);
      setFunnelPendingNodeId(null);
      setFunnelError(presentLocalizedError({ key: funnelVoucher ? 'error.pixConfirmationExpired' : 'error.offerProcessing', kind: 'payment', action: 'retry' }));
    }, pollTimeoutMsForVoucher(funnelVoucher));
    interval = window.setInterval(async () => {
      try {
        const status = await fetchOrderStatus(
          checkout.checkoutId,
          funnelPendingSaleId,
          funnelHandoffId ?? undefined,
        );
        if (!active) return;
        if (status === 'PAID') {
          window.clearInterval(interval);
          window.clearTimeout(timeout);
          const paidNode = (checkout.funnel ?? []).find((node) => node.id === funnelPendingNodeId);
          const paidSaleId = funnelPendingSaleId;
          setFunnelVoucher(null);
          setFunnelPendingSaleId(null);
          setFunnelPendingNodeId(null);
          setFunnelError(null);
          if (paidNode) {
            clearHostedFunnelPending(checkout.checkoutId);
            settleFunnel(paidNode, 'paid', { saleId: paidSaleId, method: purchaseMethodForFunnel(purchaseMethod), confirm: purchaseMethodForFunnel(purchaseMethod) === 'card' });
          }
        } else if (status === 'FAILED') {
          window.clearInterval(interval);
          window.clearTimeout(timeout);
          setFunnelVoucher(null);
          setFunnelPendingSaleId(null);
          setFunnelPendingNodeId(null);
          clearHostedFunnelPending(checkout.checkoutId);
          setFunnelError(purchaseMethodForFunnel(purchaseMethod) === 'pix'
            ? presentLocalizedError({ key: 'error.pixOfferFailed', kind: 'payment' })
            : failedPaymentError(purchaseMethod, undefined));
        }
      } catch {
        return;
      }
    }, ORDER_POLL_INTERVAL_MS);
    return () => {
      active = false;
      window.clearInterval(interval);
      window.clearTimeout(timeout);
    };
  }, [phase, funnelBusy, funnelPendingSaleId, funnelPendingNodeId, isMock, preview, funnelVoucher, funnelHandoffId, checkout.checkoutId, checkout.checkoutSessionId, checkout.funnel, funnelDecisions, purchaseMethod]);

  useEffect(() => {
    if (
      phase !== 'done' ||
      originalSaleId === null ||
      funnelHandoffId === null ||
      externalNavigationRef.current ||
      isMock ||
      preview
    ) {
      return;
    }
    let active = true;
    setOrderSummaryLoading(true);
    void afterPurchaseTracking(originalSaleId, () => {
      void fetchOrderSummary(checkout.checkoutId, originalSaleId, funnelHandoffId)
        .then((summary) => {
          if (active) {
            setOrderSummary(summary);
            setOrderSummaryLoading(false);
          }
        })
        .catch(() => {
          if (active) {
            setOrderSummary(null);
            setOrderSummaryLoading(false);
          }
        });
    });
    return () => {
      active = false;
    };
  }, [phase, originalSaleId, funnelHandoffId, isMock, preview, checkout.checkoutId]);

  useEffect(() => {
    if (phase !== 'pending' || pendingFreeTrial || preview) {
      return;
    }
    if (voucher?.pixCopyPaste || voucher?.pixQrCodeBase64) {
      trackPixGenerated(checkout, utm);
    }
  }, [phase, pendingFreeTrial, preview, voucher, checkout, utm]);

  const checkoutToastError =
    orderError !== null && orderError.field === undefined
      ? orderError
      : couponError !== null && couponError.field === undefined
        ? couponError
        : null;
  const checkoutToastId = checkoutToastError?.field === undefined
    ? 'checkout-error-toast'
    : `checkout-error-${checkoutToastError.field}`;

  useEffect(() => {
    setCheckoutToastDismissed(false);
  }, [checkoutToastError]);

  const checkoutToast = checkoutToastDismissed ? null : checkoutToastError;

  const activeMethod: PaymentMethod = methods.includes(method) ? method : methods[0] ?? 'card';
  const isPix = activeMethod === 'pix';

  const visibleBumps = isFreeTrial
    ? []
    : isPix
      ? bumps.filter((item) => item.subscription === undefined)
      : bumps;
  const selectedBumps = visibleBumps.filter((item) => selectedBumpIds.has(item.id));
  const selectedRecurringBumps = selectedBumps.filter((item) => item.subscription !== undefined);

  const basePrice: Money | undefined = selectedPlan?.trialPrice ?? selectedPlan?.value ?? offer?.price;
  const items: Money[] = [];
  if (basePrice) items.push(basePrice);
  for (const item of selectedBumps) items.push(bumpTodayAmount(item));

  let baseTotal: Money | null;
  try {
    // Core preserves quoted precision; unquoted payment lines are rounded individually.
    baseTotal = sumMoney(checkout.pricing === undefined ? items.map(roundToCurrency) : items);
  } catch {
    baseTotal = null;
  }

  let total: Money | null = baseTotal;
  let couponDiscount: Money | null = null;
  let principalDisplayPrice: Money | undefined = basePrice;
  if (basePrice && baseTotal && appliedCoupon) {
    try {
      const discountedPrincipal = roundToCurrency(
        appliedCoupon.amountAfterDiscount ?? (appliedCoupon.type === 'percent'
          ? applyPercentDiscount(basePrice, appliedCoupon.percent)
          : subtractMoney(basePrice, minMoney(appliedCoupon.amount, basePrice))));
      const principalDiscount = subtractMoney(checkout.pricing === undefined ? roundToCurrency(basePrice) : basePrice, discountedPrincipal);
      principalDisplayPrice = discountedPrincipal;
      total = subtractMoney(baseTotal, principalDiscount);
      couponDiscount = principalDiscount;
    } catch {
      total = baseTotal;
      couponDiscount = null;
    }
  }

  const checkoutCurrency = checkout.pricing?.currency ?? total?.currency ?? basePrice?.currency;
  const cardMethodTag = isSubscription ? tr('payment.recurring') : tr('payment.upfront');
  const totalLabel = total ? formatMoney(total) : tr('common.onRequest');
  const walletAmountInMinorUnits = total ? moneyToMinorUnits(total) : 0;
  const walletMethods = payment.walletMethods ?? [];
  const enteredEmail = (values.email ?? '').trim();
  const deliveryEmail = isValidEmail(enteredEmail) ? enteredEmail : undefined;
  const walletInteractionActive = activeWallet !== null;
  const walletPurchaseInProgress =
    walletInteractionActive && phase === 'processing' && (purchaseMethod === 'apple_pay' || purchaseMethod === 'google_pay');
  const activeWalletLabel = activeWallet === 'APPLE_PAY' ? 'Apple Pay' : 'Google Pay';
  const showWallets =
    !preview &&
    !isMock &&
    !isFreeTrial &&
    (phase === 'idle' || walletPurchaseInProgress) &&
    payment.gateway === 'STRIPE' &&
    payment.walletsEnabled === true &&
    Boolean(payment.onBehalfOf) &&
    walletMethods.length > 0 &&
    payment.publishableKey.length > 0 &&
    walletAmountInMinorUnits > 0;
  const walletPrimary = showWallets && walletAvailability === 'available';
  const manualPaymentVisible =
    !showWallets || walletAvailability === 'unavailable' || alternativePaymentOpen;
  const stripeCardShouldShow = useStripeCard && manualPaymentVisible && !walletInteractionActive && activeMethod === 'card';
  // O skin antigo e coluna unica: a coluna lateral fica escondida e o bump com ela.
  const showBumpsInSidebar =
    isDesktopLayout && presentation.template !== 'minimal' && resolvedSkin !== 'legacy';

  useEffect(() => {
    setWalletAvailability(showWallets ? 'probing' : 'unavailable');
  }, [showWallets]);
  useEffect(() => {
    if (stripeCardShouldShow) setStripeCardMounted(true);
  }, [stripeCardShouldShow]);
  const recurringPlanLabel = selectedPlan ? `${formatMoney(selectedPlan.value)}${priceSuffix}` : null;
  const subscriptionTodayDiffers =
    isSubscription && selectedPlan !== null && total !== null && !moneyEquals(total, selectedPlan.value);
  const recurringSummary = recurringChargeSummary([
    ...(selectedPlan && isSubscription ? [selectedPlan] : []),
    ...selectedRecurringBumps.map((item) => item.subscription!),
  ]);
  const recurringSummaryLabel = recurringChargeSummaryLabel(recurringSummary);
  const showTodayVsRecurring = subscriptionTodayDiffers || selectedRecurringBumps.length > 0;
  const summaryTotalLabel = recurringSummaryLabel ?? `${totalLabel}${priceSuffix}`;
  const summaryHeaderLabel =
    isSubscription && !showTodayVsRecurring && recurringSummaryLabel !== null ? summaryTotalLabel : totalLabel;
  const summaryThumbUrl = productThumbUrl;
  const showSummaryProductPrice = !isSubscription;
  const summaryProductClassName = [
    'vp-summary-product',
    !summaryThumbUrl ? 'vp-summary-product--no-thumb' : '',
    !showSummaryProductPrice ? 'vp-summary-product--no-price' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const couponPriceReady = !couponLoading && (appliedCoupon === null || (
    appliedCoupon.pricingToken === checkout.pricing?.pricingToken && appliedCoupon.offerId === checkout.offer?.offerId
  ));
  const canPay = Boolean(
    basePrice &&
    total &&
    !priceBlocked &&
    phase !== 'processing' &&
    couponPriceReady &&
    !walletInteractionActive &&
    (!isSubscription || methods.includes('card')),
  );
  const formLocked = phase === 'processing';
  const ctaClass = `vp-cta ${isPix ? 'vp-cta--pix' : ''}`;
  const cardBrand = cardBrandLabel(cardNumber);
  const paidFunnelDecisions = funnelDecisions.filter((decision) => decision.status === 'paid');
  const reservedFunnelDecisions = funnelDecisions.filter((decision) => decision.status === 'reserved');
  const paidOneTimeFunnelAmounts = paidFunnelDecisions.flatMap((decision) => {
    const node = funnel.find((candidate) => candidate.id === decision.nodeId);
    return node && node.subscription === undefined ? [node.price] : [];
  });
  let paidNowDisplayTotal: Money | null = total;
  if (total !== null && paidOneTimeFunnelAmounts.length > 0) {
    try {
      paidNowDisplayTotal = sumMoney([total, ...paidOneTimeFunnelAmounts]);
    } catch {
      paidNowDisplayTotal = total;
    }
  }
  const currentPaymentLabel = paymentMethodLabel(purchaseMethod);
  const backendConfirmationLines: ConfirmationLine[] =
    orderSummary?.charges.map((charge) => {
      const funnelNode = charge.funnelNodeId ? funnel.find((candidate) => candidate.id === charge.funnelNodeId) : undefined;
      const lineSubscription = charge.kind === 'principal' ? selectedPlan ?? subscription : funnelNode?.subscription;
      const recurring = lineSubscription !== undefined && lineSubscription !== null;
      return {
        id: charge.saleId,
        couponCode: charge.couponCode,
        coupon: charge.coupon,
        title: charge.title ?? (charge.kind === 'principal' ? offer?.name ?? product.name : charge.kind === 'downsell' ? 'Downsell' : 'Upsell'),
        amount: charge.amountCharged,
        amountLabel: formatMoney(charge.amountCharged),
        statusLabel: recurring && charge.status === 'PAID' ? tr('payment.firstChargePaid') : chargeStatusLabel(charge.status),
        categoryLabel: recurring ? tr('subscription.single') : tr('subscription.oneTime'),
        methodLabel:
          [
            corePaymentMethodLabel(charge.paymentMethod),
            charge.cardBrand && charge.cardLast4 ? tr('payment.cardEnding', { brand: charge.cardBrand, last4: charge.cardLast4 }) : undefined,
          ]
            .filter(Boolean)
            .join(' · ') || undefined,
        renewalLabel: subscriptionRenewalLabel(lineSubscription),
        recurring,
        paid: charge.status === 'PAID',
        sourceKind: charge.kind,
      };
    }) ?? [];
  const principalConfirmation = backendConfirmationLines.find((line) => line.sourceKind === 'principal');
  const localCheckoutChargeLines: ConfirmationLine[] = [
    ...(offer || product
      ? [
          {
            id: 'principal',
            couponCode: orderSummary !== null
              ? orderSummary.charges.find((charge) => charge.kind === 'principal')?.couponCode
              : appliedCoupon?.code,
            coupon: orderSummary !== null
              ? orderSummary.charges.find((charge) => charge.kind === 'principal')?.coupon
              : appliedCoupon && basePrice && couponDiscount ? {
                code: appliedCoupon.code,
                type: appliedCoupon.type,
                ...(appliedCoupon.type === 'percent' ? { percent: appliedCoupon.percent } : { configuredAmount: appliedCoupon.amount }),
                amountBeforeDiscount: basePrice,
                discountAmount: couponDiscount,
              } : undefined,
            title: offer?.name ?? product.name,
            amount: principalDisplayPrice ?? null,
            amountLabel: principalDisplayPrice ? formatMoney(principalDisplayPrice) : totalLabel,
            statusLabel: principalConfirmation?.statusLabel ?? (isSubscription ? tr('payment.firstChargePaid') : tr('payment.approved')),
            categoryLabel: isSubscription ? tr('subscription.single') : tr('subscription.oneTime'),
            methodLabel: currentPaymentLabel,
            renewalLabel: subscriptionRenewalLabel(selectedPlan ?? subscription),
            recurring: isSubscription,
            paid: principalConfirmation?.paid ?? true,
            sourceKind: 'principal' as const,
          },
        ]
      : []),
    ...selectedBumps.map((item) => ({
      id: `bump:${item.id}`,
      title: item.title,
      amount: bumpTodayAmount(item),
      amountLabel: formatMoney(bumpTodayAmount(item)),
      statusLabel: principalConfirmation?.paid === false ? principalConfirmation.statusLabel : item.subscription ? (bumpIsFreeTrial(item) ? tr('payment.freeTrialStarted') : tr('payment.firstChargePaid')) : tr('payment.approved'),
      categoryLabel: item.subscription ? tr('subscription.single') : tr('subscription.oneTime'),
      methodLabel: currentPaymentLabel,
      renewalLabel: subscriptionRenewalLabel(item.subscription),
      recurring: item.subscription !== undefined,
      paid: principalConfirmation?.paid ?? true,
      sourceKind: 'bump' as const,
    })),
  ];
  const localConfirmationLines: ConfirmationLine[] = [
    ...localCheckoutChargeLines,
    ...paidFunnelDecisions
      .flatMap((decision): ConfirmationLine[] => {
        const node = funnel.find((candidate) => candidate.id === decision.nodeId);
        if (!node) return [];
        const amount = node.subscription?.trialPrice ?? (node.subscription?.trialDays ? zeroMoney(node.subscription.value.currency) : undefined) ?? node.subscription?.value ?? node.price;
        return [{
          id: `funnel:${decision.nodeId}`,
          title: node.title,
          amount,
          amountLabel: formatMoney(amount),
          statusLabel: node.subscription ? tr('payment.firstChargePaid') : tr('payment.approved'),
          categoryLabel: node.subscription ? tr('subscription.single') : tr('subscription.oneTime'),
          methodLabel: paymentMethodLabel(decision.method ?? purchaseMethod),
          renewalLabel: subscriptionRenewalLabel(node.subscription ?? null),
          recurring: node.subscription !== undefined,
          paid: true,
          sourceKind: node.kind,
        }];
      }),
    ...reservedFunnelDecisions
      .flatMap((decision): ConfirmationLine[] => {
        const node = funnel.find((candidate) => candidate.id === decision.nodeId);
        if (!node) return [];
        return [{
          id: `reserved:${decision.nodeId}`,
          title: node.title,
          amount: node.price,
          amountLabel: formatMoney(node.price),
          statusLabel: tr('payment.awaitingPix'),
          categoryLabel: tr('subscription.oneTime'),
          methodLabel: tr('payment.pix'),
          recurring: false,
          paid: false,
          sourceKind: node.kind,
        }];
      }),
  ];
  const confirmationLines =
    backendConfirmationLines.length > 0
      ? selectedBumps.length > 0
        ? [
            ...localCheckoutChargeLines,
            ...backendConfirmationLines.filter((line) => line.sourceKind !== 'principal'),
          ]
        : backendConfirmationLines
      : localConfirmationLines;
  const paidNowLines = confirmationLines.filter((line) => line.paid !== false);
  const pendingConfirmationLines = confirmationLines.filter((line) => line.paid === false);
  const recurringLines = paidNowLines.filter((line) => line.recurring);
  const backendPaidNowDisplayLabel = orderSummary?.totalCaptured
    ? formatMoney(orderSummary.totalCaptured)
    : orderSummary !== null
      ? totalLabelFromLines(paidNowLines) ?? formatMoney({ amount: '0.000', currency: orderSummary.charges[0]?.amountCharged.currency ?? 'BRL' })
      : null;
  const paidNowDisplayLabel =
    backendPaidNowDisplayLabel ?? totalLabelFromLines(paidNowLines) ?? (paidNowDisplayTotal ? formatMoney(paidNowDisplayTotal) : totalLabel);

  function toggleBump(id: string) {
    if (phase === 'processing') return;
    setSelectedBumpIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function setField(key: CheckoutFieldKey, value: string) {
    setFieldError((current) => {
      if (current?.field !== key) return current;
      if (key !== 'email' || isValidEmail(value.trim())) return null;
      const walletError = current.messageKey === 'error.emailWalletRequired'
        || current.messageKey === 'error.emailWalletInvalid';
      return presentValidationError({
        key: walletError
          ? value.trim() ? 'error.emailWalletInvalid' : 'error.emailWalletRequired'
          : 'error.emailInvalid',
        field: 'email',
      });
    });
    setOrderError((current) => (current?.field === key ? null : current));
    setValues((prev) => ({ ...prev, [key]: formatCheckoutFieldInput(key, value, isBrazilBuyer) }));
  }

  async function captureContactOnBlur() {
    const initialSessionId = checkout.checkoutSessionId;
    if (preview || isMock || (!quoteMode && initialSessionId === undefined)) {
      return;
    }
    const email = (values.email ?? '').trim();
    if (!isValidEmail(email)) {
      return;
    }
    const name = (values.name ?? '').trim();
    const phone = checkoutFields.phone ? toE164Phone(values.phone ?? '', isBrazilBuyer) : '';
    const input = {
      email,
      ...(name.length > 0 ? { name } : {}),
      ...(phone.length > 0 ? { phone } : {}),
    };
    let targetCheckout = checkout;
    let targetSessionId = initialSessionId;
    let signature = targetSessionId === undefined ? null : checkoutContactSignature(targetSessionId, input.email, input.name, input.phone);
    if (signature !== null && lastCapturedContactRef.current === signature) {
      return;
    }

    try {
      if (ensureCheckoutSession !== undefined) targetSessionId = await ensureCheckoutSession();
      if (targetSessionId === undefined) return;
      signature = checkoutContactSignature(targetSessionId, input.email, input.name, input.phone);
      if (lastCapturedContactRef.current === signature) return;
      let result = await captureCheckoutContact(
        targetCheckout.checkoutId,
        targetSessionId,
        input,
      );
      if (!result.captured && (ensureCheckoutSession !== undefined || refreshCheckout !== undefined)) {
        if (ensureCheckoutSession !== undefined) {
          targetSessionId = await ensureCheckoutSession(true);
        } else {
          targetCheckout = await refreshCheckout!();
          targetSessionId = targetCheckout.checkoutSessionId;
        }
        if (targetSessionId === undefined) return;
        result = await captureCheckoutContact(
          targetCheckout.checkoutId,
          targetSessionId,
          input,
        );
        signature = checkoutContactSignature(targetSessionId, input.email, input.name, input.phone);
      }
      if (result.captured) {
        lastCapturedContactRef.current = signature;
      }
    } catch {
    }
  }

  useEffect(() => {
    if (appliedCoupon === null || (appliedCoupon.pricingToken === checkout.pricing?.pricingToken && appliedCoupon.offerId === checkout.offer?.offerId)) return;
    let active = true;
    setCouponLoading(true);
    void previewCoupon(checkout.checkoutId, appliedCoupon.code, checkout.pricing?.pricingToken, checkout.offer?.offerId)
      .then(result => {
        if (!active) return;
        setCouponLoading(false);
        if (result.valid) {
          setAppliedCoupon({ ...result, pricingToken: checkout.pricing?.pricingToken, offerId: checkout.offer?.offerId });
          setCouponError(null);
        } else {
          setCouponError(presentLocalizedError({ key: 'coupon.invalid', kind: 'validation', field: 'coupon' }));
        }
      })
      .catch(() => {
        if (active) {
          setCouponLoading(false);
          setCouponError(presentLocalizedError({ key: 'coupon.error', kind: 'system', action: 'retry' }));
        }
      });
    return () => { active = false; };
  }, [appliedCoupon, checkout.checkoutId, checkout.pricing?.pricingToken, checkout.offer?.offerId]);

  async function applyCoupon() {
    const code = couponCode.trim();
    if (!code || preview) return;
    setCouponLoading(true);
    setCouponError(null);
    try {
      const result = await previewCoupon(checkout.checkoutId, code, checkout.pricing?.pricingToken, checkout.offer?.offerId);
      if (result.valid && result.type === 'percent') {
        setAppliedCoupon({ ...result, code: code.toUpperCase(), pricingToken: checkout.pricing?.pricingToken, offerId: checkout.offer?.offerId });
        setCouponOpen(false);
        setCouponCode('');
      } else if (result.valid && result.type === 'fixed') {
        setAppliedCoupon({ ...result, code: code.toUpperCase(), pricingToken: checkout.pricing?.pricingToken, offerId: checkout.offer?.offerId });
        setCouponOpen(false);
        setCouponCode('');
      } else if (!result.valid && result.reason === 'NAO_APLICAVEL') {
        setCouponError(presentLocalizedError({ key: 'coupon.notApplicable', kind: 'validation', field: 'coupon' }));
      } else {
        setCouponError(presentLocalizedError({ key: 'coupon.invalid', kind: 'validation', field: 'coupon' }));
      }
    } catch {
      setCouponError(presentLocalizedError({ key: 'coupon.error', kind: 'system', action: 'retry' }));
    } finally {
      setCouponLoading(false);
    }
  }

  function removeCoupon() {
    setAppliedCoupon(null);
    setCouponError(null);
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (preview || !canPay || submittingRef.current) return;

    submittingRef.current = true;
    setPhase('processing');
    try {
      if (checkoutAttemptSnapshotRef.current !== null) {
        await submitCheckoutPayment();
        return;
      }
      if (checkoutAttemptSaleIdRef.current !== null) {
        await reconcilePersistedCheckoutAttempt();
        return;
      }
      let paymentMethodToken: string | undefined;
      if (activeMethod === 'card') {
        if (isMock) {
          paymentMethodToken = 'pm_card_visa';
        } else if (useStripeCard) {
          const tokenResult = await cardRef.current?.createPaymentMethod();
          if (!tokenResult || 'error' in tokenResult) {
            setOrderError(tokenResult?.error ?? presentLocalizedError({ key: 'error.cardDetails', kind: 'payment' }));
            setPhase('idle');
            return;
          }
          paymentMethodToken = tokenResult.token;
        }
      }

      await submitCheckoutPayment({
        purchaseMethod: activeMethod,
        paymentMethod: coreMethod(activeMethod),
        ...(paymentMethodToken ? { paymentMethodToken } : {}),
      });
    } finally {
      submittingRef.current = false;
    }
  }

  async function handleWalletConfirm(payload: WalletConfirmPayload) {
    if (priceBlocked) throw new CheckoutFlowError(presentCheckoutOrderError({ bloqueioCodigo: 'PRECO_ALTERADO' }));
    if (!couponPriceReady) throw new CheckoutFlowError(
      presentLocalizedError({ key: 'error.priceRefreshed', kind: 'payment', action: 'retry' }),
    );
    await submitCheckoutPayment({
      purchaseMethod: walletPurchaseMethod(payload.paymentMethod),
      paymentMethod: payload.paymentMethod,
      stripeConfirmationTokenId: payload.stripeConfirmationTokenId,
      confirmAction: payload.confirmAction,
      failWalletSheet: true,
    });
  }

  function handleBeforeWalletOpen(): boolean {
    if (priceBlocked) return false;
    if (!couponPriceReady) return false;
    const buyerEmail = (values.email ?? '').trim();
    if (!buyerEmail) {
      const error = presentValidationError({ key: 'error.emailWalletRequired', field: 'email' });
      setFieldError(error);
      setOrderError(null);
      emailInputRef.current?.focus();
      return false;
    }
    if (!isValidEmail(buyerEmail)) {
      const error = presentValidationError({ key: 'error.emailWalletInvalid', field: 'email' });
      setFieldError(error);
      setOrderError(null);
      emailInputRef.current?.focus();
      return false;
    }

    const payerDocumentNumber = checkoutFields.document ? onlyDigits(values.document ?? '') : '';
    if (payerDocumentNumber.length > 0 && !isValidCpf(payerDocumentNumber)) {
      const error = presentValidationError({ key: 'error.documentWalletInvalid', field: 'document' });
      setFieldError(error);
      setOrderError(null);
      documentInputRef.current?.focus();
      return false;
    }

    setFieldError(null);
    setOrderError(null);
    return true;
  }

  function handleWalletActivityChange(paymentMethod: WalletPaymentMethod | null) {
    setActiveWallet(paymentMethod);
    if (paymentMethod !== null && !priceBlocked) {
      recordActivity();
      setFieldError(null);
      setOrderError(null);
    }
  }

  function handleWalletAvailabilityChange(available: boolean) {
    setWalletAvailability(available ? 'available' : 'unavailable');
    if (!available) {
      setAlternativePaymentOpen(true);
    }
  }

  function handleWalletError(error: CheckoutErrorDescriptor) {
    setOrderError(current => current?.code === 'PRECO_ALTERADO' ? current : error);
    setAlternativePaymentOpen(true);
    setPhase('idle');
  }

  async function submitCheckoutPayment(input?: {
    purchaseMethod: PurchaseMethod;
    paymentMethod: CheckoutCorePaymentMethod;
    paymentMethodToken?: string;
    stripeConfirmationTokenId?: string;
    installments?: number;
    confirmAction?: (clientSecret: string) => Promise<{ ok: true } | { error: CheckoutErrorDescriptor }>;
    failWalletSheet?: boolean;
  }) {
    if (priceBlocked) return;
    const preservedAttempt = checkoutAttemptSnapshotRef.current;
    if (preservedAttempt === null && input === undefined) {
      return;
    }
    if (preservedAttempt === null && checkoutAttemptSaleIdRef.current !== null) {
      await reconcilePersistedCheckoutAttempt();
      return;
    }
    if (preservedAttempt === null && (!offer || total === null)) {
      failBeforeSubmit(presentLocalizedError({ key: 'error.checkoutUnavailable', kind: 'business' }), input?.failWalletSheet);
      return;
    }
    if (
      preservedAttempt === null &&
      isSubscription &&
      (input!.paymentMethod === 'PIX' || input!.paymentMethod === 'BOLETO')
    ) {
      failBeforeSubmit(presentLocalizedError({ key: 'error.subscriptionCardOnly', kind: 'business' }), input?.failWalletSheet);
      return;
    }

    const buyerEmail = (values.email ?? '').trim();
    if (preservedAttempt === null && !isValidEmail(buyerEmail)) {
      failBeforeSubmit(
        presentValidationError({ key: 'error.emailInvalid', field: 'email' }),
        input?.failWalletSheet,
      );
      return;
    }

    const payerDocumentNumber = checkoutFields.document ? onlyDigits(values.document ?? '') : '';
    if (preservedAttempt === null && payerDocumentNumber.length > 0 && !isValidCpf(payerDocumentNumber)) {
      failBeforeSubmit(
        presentValidationError({ key: 'error.documentInvalid', field: 'document' }),
        input?.failWalletSheet,
      );
      return;
    }
    const payerName = (values.name ?? '').trim();
    const payerPhone = checkoutFields.phone ? onlyDigits(values.phone ?? '') : '';

    setFieldError(null);
    setOrderError(null);
    setSessionEnsureFailed(false);
    setFunnelHandoffId(null);
    setFunnelDecisions([]);
    setFunnelVoucher(null);
    setFunnelPendingSaleId(null);
    setFunnelPendingNodeId(null);
    setFunnelConfirmation(null);
    setOrderSummary(null);
    setPurchaseMethod(preservedAttempt?.purchaseMethod ?? input!.purchaseMethod);
    setPhase('processing');
    trackAddPaymentInfo(checkout, utm);

    const generatedSaleId = preservedAttempt?.orderInput.saleId ?? makeSaleId();
    let attempt = preservedAttempt ?? {
      checkoutId: checkout.checkoutId,
      orderInput: {
        buyerLocale: currentLocale(),
        displayedAmount: roundToCurrency(total!),
        saleId: generatedSaleId,
        ...(checkout.checkoutSessionId ? { checkoutSessionId: checkout.checkoutSessionId } : {}),
        offerId: offer!.offerId,
        ...(isSubscription && selectedPlan ? { planId: selectedPlan.planId } : {}),
        paymentMethod: input!.paymentMethod,
        ...(input!.paymentMethodToken ? { paymentMethodToken: input!.paymentMethodToken } : {}),
        ...(input!.stripeConfirmationTokenId ? { stripeConfirmationTokenId: input!.stripeConfirmationTokenId } : {}),
        buyerEmail,
        ...(checkout.pricing !== undefined
          ? { currency: checkout.pricing.currency, pricingToken: checkout.pricing.pricingToken }
          : {}),
        ...(payerName ? { payerName } : {}),
        ...(payerPhone ? { phone: payerPhone } : {}),
        ...(payerDocumentNumber ? { payerDocument: { type: 'CPF' as const, number: payerDocumentNumber } } : {}),
        ...(affiliateRef ? { affiliateRef } : {}),
        ...(utm !== undefined ? { utm } : {}),
        ...(trackingIdentifiers !== undefined ? { trackingIdentifiers } : {}),
        ...(recuperaaiCartId ? { recuperaaiCartId } : {}),
        piiConsent: piiTargetLabel !== null && piiConsent,
        ...(selectedBumps.length > 0 ? { selectedBumpIds: selectedBumps.map((item) => item.id) } : {}),
        ...(appliedCoupon ? { couponCode: appliedCoupon.code } : {}),
        ...(input!.installments !== undefined ? { installments: input!.installments } : {}),
      },
      purchaseMethod: input!.purchaseMethod,
      ...(input!.confirmAction !== undefined ? { confirmAction: input!.confirmAction } : {}),
      ...(input!.failWalletSheet !== undefined ? { failWalletSheet: input!.failWalletSheet } : {}),
    } satisfies CheckoutAttemptSnapshot;
    checkoutAttemptSaleIdRef.current = generatedSaleId;
    checkoutAttemptSnapshotRef.current = attempt;
    if (!quoteMode || preview || preservedAttempt !== null) {
      persistCheckoutAttemptSaleId(checkout.checkoutSessionId, generatedSaleId);
    }
    try {
      if (quoteMode && !preview && preservedAttempt === null) {
        try {
          const sessionId = await ensureCheckoutSession?.();
          if (sessionId === undefined) throw new Error('Checkout session unavailable');
          checkoutAttemptStorageSessionIdRef.current = sessionId;
          const persistedSaleId = loadCheckoutAttemptSaleId(sessionId);
          if (persistedSaleId !== null) {
            checkoutAttemptSaleIdRef.current = persistedSaleId;
            checkoutAttemptSnapshotRef.current = null;
            await reconcilePersistedCheckoutAttempt();
            return;
          }
          attempt = {
            ...attempt,
            orderInput: { ...attempt.orderInput, checkoutSessionId: sessionId },
          };
          checkoutAttemptSnapshotRef.current = attempt;
          persistCheckoutAttemptSaleId(sessionId, generatedSaleId);
        } catch {
          clearCheckoutAttemptSaleId(attempt.orderInput.checkoutSessionId);
          const error = presentSystemError(undefined, 'retry');
          setSessionEnsureFailed(true);
          setOrderError(error);
          setPhase('idle');
          if (attempt.failWalletSheet) throw new CheckoutFlowError(error);
          return;
        }
      }
      let result: CheckoutOrderResult;
      try {
        result = await createCheckoutOrder(attempt.checkoutId, attempt.orderInput);
      } catch (error) {
        if (
          preservedAttempt === null &&
          checkoutBlockCode(error) === 'PRICING_TOKEN_INVALIDO' &&
          refreshCheckout !== undefined
        ) {
          const refreshed = await refreshCheckout();
          if (appliedCoupon !== null || JSON.stringify([refreshed.offer?.price, refreshed.orderBumps.map(bump => [bump.price, bump.subscription]), refreshed.subscription, refreshed.plans]) !==
            JSON.stringify([checkout.offer?.price, checkout.orderBumps.map(bump => [bump.price, bump.subscription]), checkout.subscription, checkout.plans])) {
            checkoutAttemptSnapshotRef.current = null;
            clearCheckoutAttemptSaleId();
            setOrderError(presentLocalizedError({ key: 'error.priceRefreshed', kind: 'payment', action: 'retry' }));
            setPhase('idle');
            return;
          }
          attempt = {
            ...attempt,
            checkoutId: refreshed.checkoutId,
            orderInput: {
              ...attempt.orderInput,
              ...(refreshed.checkoutSessionId !== undefined
                ? { checkoutSessionId: refreshed.checkoutSessionId }
                : {}),
              ...(refreshed.pricing !== undefined
                ? {
                    currency: refreshed.pricing.currency,
                    pricingToken: refreshed.pricing.pricingToken,
                  }
                : {}),
            },
          };
          checkoutAttemptSnapshotRef.current = attempt;
          result = await createCheckoutOrder(attempt.checkoutId, attempt.orderInput);
        } else {
          throw error;
        }
      }
      if (result.aberto === false) {
        const blockError = presentCheckoutOrderError(result);
        if (quoteMode && result.bloqueioCodigo === 'CONFIGURACAO_FINANCEIRA_ALTERADA') {
          if (preservedAttempt === null) clearCheckoutAttemptSaleId(attempt.orderInput.checkoutSessionId);
          try {
            await ensureCheckoutSession?.(true);
          } catch {
          }
          setSessionEnsureFailed(false);
          const error = previousAttemptError(blockError, preservedAttempt !== null);
          setOrderError(error);
          setPhase('idle');
          if (attempt.failWalletSheet) throw new CheckoutFlowError(error);
          return;
        }
        const priceChanged = result.bloqueioCodigo === 'PRECO_ALTERADO';
        const error = preservedAttempt === null || priceChanged
          ? blockError
          : presentLocalizedError({
              key: 'error.previousAttempt',
              kind: blockError.kind,
              values: { message: blockError.message },
            });
        setOrderError(error);
        if (priceChanged) setPriceBlocked(true);
        if (preservedAttempt === null || priceChanged) {
          clearCheckoutAttemptSaleId();
        }
        setPhase('idle');
        if (attempt.failWalletSheet) {
          throw new CheckoutFlowError(error);
        }
        return;
      }
      if (result.status === 'AMBIGUOUS') {
        const error = presentLocalizedError({ key: 'error.ambiguous', kind: 'payment' });
        setOrderError(error);
        setPhase('idle');
        if (attempt.failWalletSheet) {
          throw new CheckoutFlowError(error);
        }
        return;
      }
      if (result.isFreeTrial) {
        if (result.status === 'REQUIRES_ACTION') {
          if (useStripeCard && result.clientSecret) {
            const action = await cardRef.current?.confirmAction(result.clientSecret);
            if (!action || 'error' in action) {
              const error = action?.error ?? presentLocalizedError({ key: 'error.authentication', kind: 'payment' });
              setOrderError(error);
              setPhase('idle');
              return;
            }
            setPendingFreeTrial(true);
            setPendingSaleId(result.saleId ?? generatedSaleId);
            setPhase('pending');
            return;
          }
          setPhase('requires-action');
          return;
        }
        if (result.status !== 'PAID') {
          if (result.status === 'PENDING') {
            setPendingFreeTrial(true);
            setPendingSaleId(result.saleId ?? generatedSaleId);
            setPhase('pending');
            return;
          }
          setOrderError(failedPaymentError(attempt.purchaseMethod, result.declineCode, true));
          clearCheckoutAttemptSaleId();
          setPhase('idle');
          return;
        }
        clearCheckoutAttemptSaleId();
        setTrialStarted(true);
        setPhase('done');
        return;
      }
      if (result.status === 'PAID') {
        advanceAfterPurchase(result.saleId ?? generatedSaleId, attempt.purchaseMethod, result.funnelHandoffId);
        return;
      }
      if (result.status === 'PENDING') {
        setOriginalSaleId(result.saleId ?? generatedSaleId);
        setFunnelHandoffId(result.funnelHandoffId ?? null);
        setVoucher(result.voucher ?? null);
        setPendingSaleId(result.saleId ?? null);
        setPhase('pending');
        return;
      }
      if (result.status === 'REQUIRES_ACTION') {
        const confirmAction = attempt.confirmAction ?? cardRef.current?.confirmAction;
        if (result.clientSecret && confirmAction) {
          const action = await confirmAction(result.clientSecret);
          if (!action || 'error' in action) {
            const error = action?.error ?? presentLocalizedError({ key: 'error.authentication', kind: 'payment' });
            setOrderError(error);
            setPhase('idle');
            if (attempt.failWalletSheet) {
              throw new CheckoutFlowError(error);
            }
            return;
          }
          setOriginalSaleId(result.saleId ?? generatedSaleId);
          setFunnelHandoffId(result.funnelHandoffId ?? null);
          setPendingSaleId(result.saleId ?? null);
          setPhase('pending');
          return;
        }
        setPhase('requires-action');
        return;
      }
      const error = failedPaymentError(attempt.purchaseMethod, result.declineCode);
      setOrderError(error);
      clearCheckoutAttemptSaleId();
      setPhase('idle');
      if (attempt.failWalletSheet) {
        throw new CheckoutFlowError(error);
      }
    } catch (error) {
      if (quoteMode && checkoutBlockCode(error) === 'CONFIGURACAO_FINANCEIRA_ALTERADA') {
        const blockError = presentCheckoutOrderError(error);
        if (preservedAttempt === null) clearCheckoutAttemptSaleId(attempt.orderInput.checkoutSessionId);
        try {
          await ensureCheckoutSession?.(true);
        } catch {
        }
        setSessionEnsureFailed(false);
        const presented = previousAttemptError(blockError, preservedAttempt !== null);
        setOrderError(presented);
        setPhase('idle');
        if (attempt.failWalletSheet) throw new CheckoutFlowError(presented);
        return;
      }
      const presentedError = error instanceof CheckoutFlowError
        ? error.descriptor
        : checkoutBlockCode(error) !== undefined
          ? presentCheckoutOrderError(error)
          : presentProviderError({
            ...(error instanceof Error && error.message.length > 0 ? { message: error.message } : {}),
            fallbackKey: 'error.payment',
          });
      if (presentedError.code === 'PRECO_ALTERADO') {
        clearCheckoutAttemptSaleId(attempt.orderInput.checkoutSessionId);
        setPriceBlocked(true);
      }
      setOrderError(presentedError);
      setPhase('idle');
      if (attempt.failWalletSheet) {
        throw new CheckoutFlowError(presentedError);
      }
    }
  }

  function previousAttemptError(error: CheckoutErrorDescriptor, preserved: boolean): CheckoutErrorDescriptor {
    return preserved ? presentLocalizedError({
      key: 'error.previousAttempt', kind: error.kind, values: { message: error.message },
    }) : error;
  }

  async function refreshBlockedCheckout() {
    if (refreshCheckout === undefined || submittingRef.current) return;
    submittingRef.current = true;
    try {
      const refreshed = await refreshCheckout();
      if (appliedCoupon !== null) {
        const coupon = await previewCoupon(refreshed.checkoutId, appliedCoupon.code, refreshed.pricing?.pricingToken, refreshed.offer?.offerId);
        setAppliedCoupon(coupon.valid ? { ...coupon, pricingToken: refreshed.pricing?.pricingToken, offerId: refreshed.offer?.offerId } : null);
        setCouponError(null);
      }
      setPriceBlocked(false);
      setOrderError(null);
    } catch {
      // Preserve the block until current checkout data has been loaded successfully.
    } finally {
      setCouponLoading(false);
      submittingRef.current = false;
    }
  }

  async function retryQuoteSession() {
    try {
      const sessionId = await ensureCheckoutSession?.();
      if (sessionId === undefined) return;
      setSessionEnsureFailed(false);
      setOrderError(null);
    } catch {
    }
  }

  function failBeforeSubmit(error: CheckoutErrorDescriptor, failWalletSheet?: boolean) {
    setFieldError(error.field ? error : null);
    setOrderError(error.field === undefined ? error : null);
    setPhase('idle');
    if (error.field === 'email') {
      emailInputRef.current?.focus();
    } else if (error.field === 'document') {
      documentInputRef.current?.focus();
    }
    if (failWalletSheet) {
      throw new CheckoutFlowError(error);
    }
  }

  async function reconcilePersistedCheckoutAttempt(): Promise<void> {
    const saleId = checkoutAttemptSaleIdRef.current;
    if (saleId === null) {
      return;
    }
    setFieldError(null);
    setOrderError(null);
    setPhase('processing');
    try {
      const result = funnelHandoffId === null
        ? await fetchOrderResult(checkout.checkoutId, saleId)
        : await fetchOrderResult(checkout.checkoutId, saleId, funnelHandoffId);
      if (result.status === 'PAID') {
        if (result.isFreeTrial) {
          clearCheckoutAttemptSaleId();
          setTrialStarted(true);
          setPhase('done');
          return;
        }
        advanceAfterPurchase(saleId, activeMethod, result.funnelHandoffId ?? funnelHandoffId ?? undefined);
        return;
      }
      if (result.status === 'FAILED') {
        clearCheckoutAttemptSaleId();
        setOrderError(failedPaymentError(activeMethod, result.declineCode));
        setPhase('idle');
        return;
      }
      if (result.status === 'PENDING' || result.status === 'REQUIRES_ACTION') {
        setPendingFreeTrial(result.isFreeTrial === true);
        setPendingSaleId(saleId);
        setPhase('pending');
        return;
      }
      setOrderError(presentLocalizedError({ key: 'error.previousPending', kind: 'payment' }));
      setPhase('idle');
    } catch {
      setOrderError(presentLocalizedError({ key: 'error.previousReconciling', kind: 'payment', action: 'retry' }));
      setPhase('idle');
    }
  }

  function clearCheckoutAttemptSaleId(sessionId = checkoutAttemptStorageSessionIdRef.current) {
    checkoutAttemptSaleIdRef.current = null;
    checkoutAttemptSnapshotRef.current = null;
    forgetCheckoutAttemptSaleId(sessionId);
  }

  function redirectToThankYou(saleId: string): boolean {
    if (preview) {
      return false;
    }
    const target = thankYouUrl(checkout.thankYouUrl ?? '', saleId, funnelTrackingParameters(utm, trackingIdentifiers));
    if (target === null) return false;
    if (!externalNavigationRef.current) {
      externalNavigationRef.current = true;
      void afterPurchaseTracking(saleId, () => window.location.assign(target));
    }
    return true;
  }

  function redirectToExternalFunnel(
    node: PublicCheckout['funnel'][number],
    saleId: string,
    handoffId: string | null | undefined,
  ): boolean {
    if (preview || node.mode !== 'snippet' || !handoffId) return false;
    const target = externalFunnelUrl(node.externalPageUrl, node.id, handoffId, saleId, funnelTrackingParameters(utm, trackingIdentifiers));
    if (target === null) return false;
    if (!externalNavigationRef.current) {
      externalNavigationRef.current = true;
      setPhase('done');
      void afterPurchaseTracking(saleId, () => window.location.assign(target));
    }
    return true;
  }

  function continueFunnel(
    decisions: readonly FunnelDecision[],
    saleId: string,
    handoffId: string | null | undefined,
  ) {
    const node = nextFunnelNode(funnel, decisions);
    if (node === null) {
      finishCheckout(saleId, handoffId);
      return;
    }
    if (node.mode === 'snippet') {
      if (!redirectToExternalFunnel(node, saleId, handoffId)) setPhase('done');
      return;
    }
    setPhase('funnel');
  }

  function advanceAfterPurchase(saleId: string, paidMethod: PurchaseMethod, handoffId?: string) {
    clearCheckoutAttemptSaleId();
    setRefreshedFunnelNodes(Object.fromEntries(funnel.map(node => [node.id, node])));
    void trackConfirmedPurchase(checkout.checkoutId, saleId, saleId, handoffId, utm);
    setOriginalSaleId(saleId);
    setFunnelHandoffId(handoffId ?? null);
    setFunnelDecisions([]);
    setOrderSummary(null);
    if (paidMethod === 'boleto') finishCheckout(saleId, handoffId);
    else continueFunnel([], saleId, handoffId);
  }

  function finishCheckout(saleId = originalSaleId, _handoffId = funnelHandoffId) {
    setPhase('done');
    if (saleId !== null && redirectToThankYou(saleId)) {
      return;
    }
  }

  function settleFunnel(
    node: PublicCheckout['funnel'][number],
    status: FunnelDecision['status'],
    details: { saleId?: string | undefined; method?: PaymentMethod | undefined; confirm?: boolean } = {},
  ) {
    clearHostedFunnelPending(checkout.checkoutId);
    if (status === 'paid' && details.saleId !== undefined && originalSaleId !== null) {
      void trackConfirmedPurchase(
        checkout.checkoutId,
        originalSaleId,
        details.saleId,
        funnelHandoffId ?? undefined,
        utm,
      );
    }
    const next = recordFunnelDecision(funnelDecisions, node, status, details);
    setFunnelDecisions(next);
    setFunnelVoucher(null);
    setFunnelPendingSaleId(null);
    setFunnelPendingNodeId(null);
    setFunnelError(null);
    const advance = () => {
      continueFunnel(next, originalSaleId ?? '', funnelHandoffId);
    };
    if (details.confirm && status === 'paid') {
      setFunnelConfirmation({
        title: tr('success.added'),
        body: tr('success.addedBody', { value: formatMoney(node.subscription?.value ?? node.price) }),
      });
      if (confirmationTimerRef.current !== null) {
        window.clearTimeout(confirmationTimerRef.current);
      }
      confirmationTimerRef.current = window.setTimeout(() => {
        setFunnelConfirmation(null);
        advance();
      }, 400);
      return;
    }
    advance();
  }

  async function handleFunnelDecision(
    node: PublicCheckout['funnel'][number],
    decision: 'ACCEPT' | 'REJECT',
  ) {
    if (originalSaleId === null || funnelBusy) return;
    setFunnelBusy(true);
    setFunnelError(null);
    try {
      const persistPending = (saleId: string | null, voucher: CheckoutVoucher | null) => {
        saveHostedFunnelPending(checkout.checkoutId, {
          originalSaleId, handoffId: funnelHandoffId, nodeId: node.id, saleId, voucher,
          method: purchaseMethodForFunnel(purchaseMethod), decisions: funnelDecisions,
        });
      };
      if (decision === 'ACCEPT') persistPending(funnelPendingSaleId, funnelVoucher);
      const submitDecision = () =>
        submitFunnelStep(checkout.checkoutId, {
          originalSaleId,
          funnelNodeId: node.id,
          decision,
          ...(funnelHandoffId !== null ? { funnelHandoffId } : {}),
        });
      let result;
      try {
        result = await submitDecision();
      } catch (error) {
        if (decision !== 'REJECT') {
          throw error;
        }
        result = await submitDecision();
      }
      if (!result.accepted) {
        clearHostedFunnelPending(checkout.checkoutId);
        settleFunnel(node, 'rejected');
        return;
      }
      if (result.aberto === false) {
        if (decision === 'ACCEPT' && checkoutBlockCode(result) === 'PRICING_TOKEN_INVALIDO' && funnelHandoffId !== null) {
          const refreshed = await fetchFunnelOffer(node.id, funnelHandoffId);
          if (refreshed !== null) {
            setRefreshedFunnelNodes(current => ({ ...current, [node.id]: refreshed.node }));
            clearHostedFunnelPending(checkout.checkoutId);
            setFunnelError(presentLocalizedError({ key: 'error.priceRefreshed', kind: 'payment', action: 'retry' }));
            return;
          }
        }
        if (decision === 'REJECT') {
          settleFunnel(node, 'rejected');
          return;
        }
        if (checkoutBlockCode(result) === 'COMPRA_NAO_PERMITIDA') {
          clearHostedFunnelPending(checkout.checkoutId);
        }
        setFunnelError(presentCheckoutOrderError(result, 'error.offerUnavailable'));
        return;
      }
      if (result.status === 'PAID') {
        clearHostedFunnelPending(checkout.checkoutId);
        settleFunnel(node, 'paid', {
          saleId: result.saleId,
          method: purchaseMethodForFunnel(purchaseMethod),
          confirm: purchaseMethodForFunnel(purchaseMethod) === 'card',
        });
        return;
      }
      if (result.status === 'PENDING' || result.status === 'REQUIRES_ACTION') {
        persistPending(result.saleId ?? null, result.voucher ?? null);
        setFunnelVoucher(result.voucher ?? null);
        setFunnelPendingSaleId(result.saleId ?? null);
        setFunnelPendingNodeId(node.id);
        if (result.status === 'REQUIRES_ACTION') {
          await authenticatePayment({
            ...(result.clientSecret ? { clientSecret: result.clientSecret } : {}),
            publishableKey: result.publishableKey ?? payment.publishableKey,
          });
        }
        return;
      }
      if (result.status === 'FAILED') clearHostedFunnelPending(checkout.checkoutId);
      setFunnelError(failedPaymentError(purchaseMethod, result.declineCode));
    } catch (error) {
      setFunnelError(
        error instanceof CheckoutFlowError ? error.descriptor : error instanceof Error
          ? presentProviderError({
              ...(error.message.length > 0 ? { message: error.message } : {}),
              fallbackKey: 'error.offerProcessing',
            })
          : presentSystemError(undefined, 'retry'),
      );
    } finally {
      setFunnelBusy(false);
    }
  }

  if (phase === 'pending') {
    const hasVoucher = Boolean(voucher?.pixCopyPaste || voucher?.pixQrCodeBase64);
    if (pendingFreeTrial) {
      return (
        <main className="vp-shell vp-shell--center">
          <section className="vp-card vp-success">
            {isMock && <span className="vp-demo-badge">{tr('common.demo')}</span>}
            <div className="vp-success-mark" aria-hidden="true">
              <ShieldIcon width={36} height={36} />
            </div>
            <h1>{tr('pending.subscriptionTitle')}</h1>
            <p>{tr('pending.subscriptionBody', { email: values.email || tr('common.emailFallback') })}</p>
            <div className="vp-success-summary">
              <span>{offer?.name ?? product.name}</span>
              <strong>{totalLabel}</strong>
            </div>
          </section>
        </main>
      );
    }
    return (
      <main className="vp-shell vp-shell--center">
        <section className="vp-card vp-success">
          {isMock && <span className="vp-demo-badge">{tr('common.demo')}</span>}
          <div className="vp-success-mark" aria-hidden="true">
            <PixIcon width={36} height={36} />
          </div>
          <h1>{hasVoucher ? tr('pix.payToFinish') : tr('pix.processingTitle')}</h1>
          {hasVoucher ? (
            <>
              <p>{tr('pix.scan')}</p>
              {voucher?.pixQrCodeBase64 && (
                <img
                  className="vp-pix-qr"
                  alt="QR Code Pix"
                  src={`data:image/png;base64,${voucher.pixQrCodeBase64}`}
                />
              )}
              {voucher?.pixCopyPaste && (
                <label className="vp-field">
                  <span>{tr('pix.copyPaste')}</span>
                  <input readOnly value={voucher.pixCopyPaste} aria-label={tr('pix.copyPaste')} />
                </label>
              )}
            </>
          ) : (
            <p>{tr('pix.processingBody', { email: values.email || tr('common.emailFallback') })}</p>
          )}
          <div className="vp-success-summary">
            <span>{offer?.name ?? product.name}</span>
            <strong>{totalLabel}</strong>
          </div>
        </section>
      </main>
    );
  }

  if (phase === 'requires-action') {
    return (
      <main className="vp-shell vp-shell--center">
        <section className="vp-card vp-success">
          {isMock && <span className="vp-demo-badge">{tr('common.demo')}</span>}
          <div className="vp-success-mark" aria-hidden="true">
            <ShieldIcon width={36} height={36} />
          </div>
          <h1>{tr('pending.authenticationTitle')}</h1>
          <p>{tr('pending.authenticationBody')}</p>
          <div className="vp-success-summary">
            <span>{offer?.name ?? product.name}</span>
            <strong>{totalLabel}</strong>
          </div>
        </section>
      </main>
    );
  }

  if (phase === 'funnel') {
    if (funnelConfirmation) {
      return (
        <main className="vp-shell vp-shell--center">
          <section className="vp-card vp-success">
            {isMock && <span className="vp-demo-badge">{tr('common.demo')}</span>}
            <div className="vp-success-mark" aria-hidden="true">
              <CheckIcon width={36} height={36} />
            </div>
            <h1>{funnelConfirmation.title}</h1>
            <p>{funnelConfirmation.body}</p>
          </section>
        </main>
      );
    }
    const node = nextFunnelNode(funnel, funnelDecisions);
    if (node?.mode === 'hosted') {
      return (
        <UpsellStep
          node={node}
          busy={funnelBusy || (funnelPendingSaleId !== null && funnelVoucher === null && funnelError === null)}
          error={funnelError}
          voucher={funnelVoucher}
          demo={isMock}
          paymentMethod={purchaseMethodForFunnel(purchaseMethod)}
          skin={resolvedSkin}
          onAccept={() => handleFunnelDecision(node, 'ACCEPT')}
          onReject={() => handleFunnelDecision(node, 'REJECT')}
          onErrorAction={() => handleFunnelDecision(node, 'ACCEPT')}
          onSkip={() => {
            clearHostedFunnelPending(checkout.checkoutId);
            if (funnelPendingSaleId !== null && funnelPendingNodeId === node.id) {
              settleFunnel(node, 'reserved', { saleId: funnelPendingSaleId, method: 'pix' });
              return;
            }
            setFunnelVoucher(null);
            setFunnelPendingSaleId(null);
            setFunnelPendingNodeId(null);
            finishCheckout();
          }}
        />
      );
    }
  }

  if (phase === 'done' && trialStarted) {
    return (
      <main className="vp-shell vp-shell--center">
        <section className="vp-card vp-success vp-success--wide">
          {isMock && <span className="vp-demo-badge">{tr('common.demo')}</span>}
          <div className="vp-success-mark" aria-hidden="true">
            <CheckIcon width={36} height={36} />
          </div>
          <h1>{tr('success.subscriptionTitle')}</h1>
          <p>
            {tr('success.freeDays', { days: selectedPlan?.trialDays ?? 0 })}
            {selectedPlan ? tr('success.firstCharge', { value: `${formatMoney(selectedPlan.value)}${priceSuffix}`, date: firstChargeLabel }) : ''}
          </p>
          <p>{tr('success.subscriptionEmail', { email: values.email || tr('common.emailFallback') })}</p>
        </section>
      </main>
    );
  }

  if (phase === 'done' && !isMock && !preview && originalSaleId !== null && funnelHandoffId !== null && orderSummary === null) {
    return <main className="vp-shell vp-shell--center">
      <section className="vp-card vp-success vp-success--wide" role="status">
        <h1>{tr(orderSummaryLoading ? 'summary.loading' : 'summary.unavailableTitle')}</h1>
        {!orderSummaryLoading && <p>{tr('summary.unavailableBody')}</p>}
      </section>
    </main>;
  }

  if (phase === 'done' || phase === 'funnel') {
    return (
      <main className="vp-shell vp-shell--center">
        <PaymentConfirmation
          email={values.email}
          paymentChanged={orderSummary?.charges.some(charge => ['REFUND_REQUESTED', 'PARTIALLY_REFUNDED', 'REFUNDED', 'CHARGED_BACK'].includes(charge.status)) ?? false}
          isMock={isMock}
          currentPaymentLabel={currentPaymentLabel}
          paidNowLines={paidNowLines}
          paidNowDisplayLabel={paidNowDisplayLabel}
          pendingConfirmationLines={pendingConfirmationLines}
          recurringLines={recurringLines}
        />
      </main>
    );
  }

  const ctaText = !basePrice
    ? tr('common.unavailable')
    : isFreeTrial
      ? tr('checkout.startTrial', { days: selectedPlan?.trialDays ?? 0 })
      : isSubscription
        ? tr('checkout.subscribeNow')
        : activeMethod === 'boleto'
          ? tr('checkout.generateBoleto', { value: totalLabel })
          : tr('checkout.payAndFinish', { value: totalLabel });

  const summary = (
    <aside className={`vp-summary-card ${summaryOpen ? 'is-open' : ''}`}>
      <button type="button" className="vp-summary-head" onClick={() => setSummaryOpen((v) => !v)}>
        <span>{tr('checkout.orderSummary')}</span>
        <span className="vp-summary-head-right">
          <strong className="vp-num">{summaryHeaderLabel}</strong>
          <ChevronIcon className="vp-summary-chevron" width={18} height={18} />
        </span>
      </button>

      <div className="vp-summary-body">
        <div className={summaryProductClassName}>
          {summaryThumbUrl && <SafeImage src={summaryThumbUrl} alt="" className="vp-summary-thumb" />}
          <div>
            <span className="vp-summary-product-name">{offer?.name ?? product.name}</span>
            <span className="vp-faint vp-small">{product.name}</span>
          </div>
          {showSummaryProductPrice && (
            <span className="vp-num">{basePrice ? `${formatMoney(basePrice)}${priceSuffix}` : tr('common.unavailable')}</span>
          )}
        </div>

        {selectedBumps.map((item) => (
          <div className="vp-summary-line" key={item.id}>
            <span>{item.title}</span>
            <span className="vp-num">
              {item.subscription
                ? `${formatMoney(bumpTodayAmount(item))} ${tr('common.today')} · ${subscriptionRenewalLabel(item.subscription)}`
                : formatMoney(item.price)}
            </span>
          </div>
        ))}

        {appliedCoupon && couponDiscount && (
          <div className="vp-summary-line">
            <span>{tr('coupon.label', { code: appliedCoupon.code })}</span>
            <span className="vp-num">−{formatMoney(couponDiscount)}</span>
          </div>
        )}

        {(acceptsCoupon || appliedCoupon !== null) && !isSubscription && (
          <div className="vp-coupon">
            {appliedCoupon ? (
              <div className="vp-coupon-field">
                <span className="vp-small">
                  {tr('coupon.applied')}
                </span>
                <button type="button" className="vp-link" onClick={removeCoupon}>
                  {tr('coupon.remove')}
                </button>
              </div>
            ) : couponOpen ? (
              <>
                <div className="vp-coupon-field">
                  <input
                    placeholder={tr('coupon.code')}
                    aria-label={tr('coupon.code')}
                    value={couponCode}
                    aria-invalid={couponError?.field === 'coupon' ? true : undefined}
                    onChange={(e) => {
                      setCouponError(null);
                      setCouponCode(e.target.value);
                    }}
                  />
                  <button
                    type="button"
                    className="vp-ghost"
                    onClick={applyCoupon}
                    disabled={couponLoading}
                  >
                    {couponLoading
                      ? <span className="vp-spinner vp-spinner--sm" aria-hidden="true" />
                      : tr('coupon.apply')}
                  </button>
                </div>
                {couponError?.field === 'coupon' && (
                  <p className="vp-field-feedback" role="alert">
                    {couponError.messageKey === undefined
                      ? couponError.message
                      : tr(couponError.messageKey, couponError.messageValues ?? {})}
                  </p>
                )}
              </>
            ) : (
              <button type="button" className="vp-link" onClick={() => setCouponOpen(true)}>
                {tr('coupon.haveOne')}
              </button>
            )}
          </div>
        )}

        {selectedPlan && selectedPlan.trialDays > 0 && (
          <div className="vp-summary-line">
            <span>{tr('summary.trialPeriod')}</span>
            <span className="vp-num">
              {tr('summary.days', { days: selectedPlan.trialDays })}
              {selectedPlan.trialPrice ? ` · ${formatMoney(selectedPlan.trialPrice)}` : ` ${tr('common.free')}`}
            </span>
          </div>
        )}

        {showTodayVsRecurring && (
          <div className="vp-summary-line">
            <span>{tr('summary.totalToday')}</span>
            <span className="vp-num">{totalLabel}</span>
          </div>
        )}

        <div className={`vp-summary-total${recurringSummaryLabel ? ' vp-summary-total--recurring' : ''}`}>
          <span className="vp-summary-total-label">
            {recurringSummaryLabel ? tr('summary.recurringCharge') : tr('common.total')}
          </span>
          {recurringSummaryLabel ? (
            <strong className="vp-num vp-summary-total-recurring-values">
              {recurringSummary.map(({ amountLabel, periodLabel }) => (
                <span className="vp-summary-total-recurring-line" key={`${amountLabel}:${periodLabel}`}>
                  <span className="vp-summary-total-amount">{amountLabel}</span>
                  <span className="vp-summary-total-period">{periodLabel}</span>
                </span>
              ))}
            </strong>
          ) : (
            <strong className="vp-num">{summaryTotalLabel}</strong>
          )}
        </div>

        <div className="vp-pricing-notes">
          {total && isPix && methods.includes('pix') && (
            <span className="vp-pix-cash">
              <PixIcon width={15} height={15} /> {tr('summary.pixUpfront', { value: totalLabel })}
            </span>
          )}
        </div>

        <div className="vp-summary-trust">
          {hasGuarantee && (
            <span>
              <ShieldIcon width={15} height={15} /> {tr('checkout.fullRefundGuarantee', { days: refundDays })}
            </span>
          )}
          {showSecurity && (
            <span>
              <LockIcon width={15} height={15} /> {tr('checkout.secureEncrypted')}
            </span>
          )}
        </div>

        <div className="vp-brands" aria-hidden="true">
          {methods.includes('pix') && <span>Pix</span>}
          {showSecurity && methods.includes('card') && <span>Visa</span>}
          {showSecurity && methods.includes('card') && <span>Master</span>}
          {showSecurity && methods.includes('card') && <span>Elo</span>}
          {methods.includes('boleto') && <span>{tr('payment.boleto')}</span>}
        </div>
      </div>
    </aside>
  );

  return (
    <div className="vp-page">
      {resolvedSkin === 'legacy' && presentation.blocks.timer && (
        <LegacyCountdown label={tr('legacy.urgency')} />
      )}
      {resolvedSkin === 'legacy' && presentation.blocks.reviews && (
        <p className="vp-legacy-reviews">{tr('legacy.reassurance')}</p>
      )}
      <header className="vp-topbar">
        <div className="vp-brand">
          <img className="vp-brand-logo" src="/brand/logo-full.png" alt="VendePay" />
        </div>
        <div className="vp-secure-tag">
          <LockIcon width={15} height={15} />
          {tr('checkout.securePurchase')}
        </div>
      </header>

      {checkoutToast && (
        <CheckoutErrorAlert
          error={checkoutToast}
          placement={priceBlocked ? 'modal' : 'toast'}
          id={checkoutToastId}
          onDismiss={() => setCheckoutToastDismissed(true)}
          {...(priceBlocked
            ? { onAction: () => { void refreshBlockedCheckout(); } }
            : sessionEnsureFailed
              ? { onAction: () => { void retryQuoteSession(); } }
              : {})}
          autoFocus={checkoutToast.kind !== 'validation'}
        />
      )}

      <main
        className="vp-shell"
        onChangeCapture={recordActivity}
        onClickCapture={(event) => {
          if ((event.target as Element).closest('button[type="button"], [role="radio"], [role="checkbox"]')) {
            recordActivity();
          }
        }}
      >
        {presentation.bannerVideoUrl && <CheckoutBannerVideo src={presentation.bannerVideoUrl} />}
        {imageBannerEnabled && !showVideoBanner && presentation.bannerUrl && (
          <div className="vp-checkout-banner" data-testid="checkout-banner-image">
            <SafeImage src={presentation.bannerUrl} alt="" className="vp-checkout-banner-image" />
          </div>
        )}
        <div className="vp-col-summary vp-col-summary--top">{summary}</div>

        <form className="vp-col-main vp-card" id="vp-checkout-form" onSubmit={onSubmit}>
          <div className="vp-product">
            {productThumbUrl && (
              <SafeImage src={productThumbUrl} alt={product.name} className="vp-product-thumb" />
            )}
            <div className="vp-product-info">
              <h1 className="vp-product-name">{product.name}</h1>
              {product.description && <p className="vp-product-desc">{product.description}</p>}
            </div>
          </div>

          {isSubscription && planOptions.length > 1 && (
            <div className="vp-plans-section">
              <h2 className="vp-plans-title">{tr('checkout.choosePlan')}</h2>
              <div className={`vp-plans ${planHasBenefits ? 'vp-plans--cards' : ''}`} role="radiogroup" aria-label={tr('checkout.choosePlan')}>
                {planOptions.map((option) => {
                  const active = option.planId === selectedPlan?.planId;
                  return (
                    <button
                      key={option.planId}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      className={`vp-plan ${active ? 'is-active' : ''}`}
                      onClick={() => setSelectedPlanId(option.planId)}
                    >
                      <span className="vp-plan-check" aria-hidden="true">
                        {active ? <CheckIcon width={14} height={14} /> : null}
                      </span>
                      <span className="vp-plan-body">
                        <span className="vp-plan-head">
                          <strong>{planOptionLabel(option)}</strong>
                          {option.planId === bestValuePlanId && <span className="vp-plan-badge">{tr('checkout.bestValue')}</span>}
                        </span>
                        {option.description && <span className="vp-plan-desc">{option.description}</span>}
                        {option.benefits && option.benefits.length > 0 && (
                          <ul className="vp-plan-benefits">
                            {option.benefits.map((benefit, index) => (
                              <li key={`${option.planId}:${index}`}>
                                <CheckIcon width={13} height={13} />
                                <span>{benefit}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </span>
                      <span className="vp-plan-price">
                        <strong className="vp-num">{formatMoney(option.value)}</strong>
                        <em>{periodSuffix(option.billingPeriodUnit, option.billingPeriodCount)}</em>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="vp-step">
            <span className="vp-step-num">1</span>
            <h2>{tr('checkout.yourDetails')}</h2>
          </div>
          <p className="vp-guest-note">{tr('checkout.guest')}</p>
          <div className="vp-fields">
            {fieldOrder
              .filter((key) => checkoutFields[key])
              .map((key) => {
                const meta = checkoutFieldMeta(key, isBrazilBuyer);
                const emailError = key === 'email' && fieldError?.field === key ? fieldError : null;
                return (
                  <label className={`vp-field ${fieldError?.field === key ? 'vp-field--invalid' : ''}`} key={key}>
                    <span id={`checkout-label-${key}`}>{meta.label}</span>
                    <input
                      ref={key === 'email' ? emailInputRef : key === 'document' ? documentInputRef : undefined}
                      type={meta.type}
                      inputMode={meta.inputMode}
                      placeholder={meta.placeholder}
                      autoComplete={meta.autoComplete}
                      value={values[key] ?? ''}
                      disabled={formLocked}
                      aria-labelledby={`checkout-label-${key}`}
                      aria-invalid={fieldError?.field === key ? true : undefined}
                      aria-describedby={emailError ? 'checkout-error-email' : undefined}
                      onInvalid={key === 'email' ? (event) => {
                        event.preventDefault();
                        failBeforeSubmit(presentValidationError({ key: 'error.emailInvalid', field: 'email' }));
                      } : undefined}
                      onChange={(e) => setField(key, e.target.value)}
                      onBlur={key === 'email' || key === 'name' || key === 'phone' ? () => void captureContactOnBlur() : undefined}
                    />
                    {emailError && (
                      <p className="vp-field-feedback" id="checkout-error-email" role="alert">
                        {emailError.messageKey ? tr(emailError.messageKey, emailError.messageValues) : emailError.message}
                      </p>
                    )}
                  </label>
                );
              })}
          </div>

          {piiTargetLabel !== null && (
            <label className="vp-consent">
              <input
                type="checkbox"
                checked={piiConsent}
                onChange={(event) => setPiiConsent(event.target.checked)}
              />
              <span className="vp-consent-text">
                <strong>{tr('pii.optional')}</strong>
                <span>{tr('pii.consent', { target: piiTargetLabel })}</span>
              </span>
            </label>
          )}

          {!showBumpsInSidebar && (
            <OrderBumpList
              items={visibleBumps}
              selectedIds={selectedBumpIds}
              onToggle={toggleBump}
              placement="inline"
              disabled={formLocked}
            />
          )}

          <div className="vp-step">
            <span className="vp-step-num">2</span>
            <h2>{tr('checkout.payment')}</h2>
          </div>
          {showWallets && walletAvailability !== 'unavailable' && (
            <p className="vp-wallet-heading">{tr('payment.express')}</p>
          )}
          {showWallets && (
            <>
              <ExpressWalletBand
                disabled={priceBlocked || phase === 'processing' || !couponPriceReady}
                amountInMinorUnits={walletAmountInMinorUnits}
                currency={checkoutCurrency ?? total?.currency ?? 'BRL'}
                publishableKey={payment.publishableKey}
                {...(payment.onBehalfOf !== undefined ? { onBehalfOf: payment.onBehalfOf } : {})}
                walletMethods={walletMethods}
                {...(deliveryEmail !== undefined ? { deliveryEmail } : {})}
                onConfirmWallet={handleWalletConfirm}
                onBeforeWalletOpen={handleBeforeWalletOpen}
                onAvailabilityChange={handleWalletAvailabilityChange}
                onWalletActivityChange={handleWalletActivityChange}
                onWalletError={handleWalletError}
              />
              {walletInteractionActive && (
                <div className="vp-panel vp-wallet-active" role="status">
                  <strong>
                    {walletPurchaseInProgress
                      ? tr('payment.processingWallet', { wallet: activeWalletLabel })
                      : tr('payment.completeWallet', { wallet: activeWalletLabel })}
                  </strong>
                  <p>
                    {tr('payment.walletHelp')}
                  </p>
                </div>
              )}
            </>
          )}
          {walletPrimary && !walletInteractionActive && (
            <button
              type="button"
              className={`vp-payment-alternatives ${alternativePaymentOpen ? 'is-open' : ''}`}
              aria-expanded={alternativePaymentOpen}
              onClick={() => setAlternativePaymentOpen((open) => !open)}
            >
              <span><CardIcon /> {tr('payment.otherMethods')}</span>
              <ChevronIcon width={18} height={18} />
            </button>
          )}

          {manualPaymentVisible && !isSubscription && !walletInteractionActive && (
            <div className="vp-methods" role="radiogroup" aria-label={tr('payment.method')} data-count={methods.length}>
            {methods.includes('pix') && (
              <button
                type="button"
                className={`vp-method ${isPix ? 'is-active is-pix' : ''}`}
                role="radio"
                aria-checked={isPix}
                disabled={formLocked}
                onClick={() => setMethod('pix')}
              >
                <PixIcon />
                <span>Pix</span>
                <em className="vp-method-tag">{tr('payment.instant')}</em>
              </button>
            )}
            {methods.includes('card') && (
              <button
                type="button"
                className={`vp-method ${activeMethod === 'card' ? 'is-active' : ''}`}
                role="radio"
                aria-checked={activeMethod === 'card'}
                disabled={formLocked}
                onClick={() => setMethod('card')}
              >
                <CardIcon />
                <span>{tr('payment.card')}</span>
                <em className="vp-method-tag">{cardMethodTag}</em>
              </button>
            )}
            {methods.includes('boleto') && (
              <button
                type="button"
                className={`vp-method ${activeMethod === 'boleto' ? 'is-active' : ''}`}
                role="radio"
                aria-checked={activeMethod === 'boleto'}
                disabled={formLocked}
                onClick={() => setMethod('boleto')}
              >
                <BarcodeIcon />
                <span>{tr('payment.boleto')}</span>
              </button>
            )}
            </div>
          )}

          {manualPaymentVisible && isSubscription && !walletInteractionActive && (
            <div className="vp-panel vp-card-method-head">
              <CardIcon />
              <span>{tr('payment.creditCard')}</span>
              <em className="vp-method-tag">{tr('payment.recurring')}</em>
            </div>
          )}

          {manualPaymentVisible && isFreeTrial && (
            <p className="vp-trial-payment-note">
              {tr('payment.subscriptionTrialNote')}
            </p>
          )}

          {manualPaymentVisible && isPix && !walletInteractionActive && (
            <div className="vp-panel vp-panel--pix">
              <div className="vp-qr" aria-hidden="true">
                <PixIcon width={40} height={40} />
              </div>
              <div>
                <strong>{tr('pix.instantTitle')}</strong>
                <p className="vp-faint vp-small">{tr('pix.instantBody')}</p>
              </div>
            </div>
          )}

          {stripeCardMounted && (
            <div style={stripeCardShouldShow ? undefined : { display: 'none' }}>
              <StripeCardPanel
                ref={cardRef}
                publishableKey={payment.publishableKey}
                cardholderName={cardName}
                onCardholderNameChange={setCardName}
                onActivity={recordActivity}
                showSecurity={showSecurity}
              />
            </div>
          )}
          {!useStripeCard && manualPaymentVisible && !walletInteractionActive && activeMethod === 'card' && (
            <div className="vp-panel vp-card-fields">
              {showSecurity && (
                <div className="vp-secure-inline">
                  <LockIcon width={14} height={14} /> {tr('payment.secureConnection')}
                </div>
              )}
              <label className="vp-field">
                <span>{tr('payment.cardNumber')}</span>
                <div className="vp-card-number-field">
                  <input
                    inputMode="numeric"
                    placeholder="0000 0000 0000 0000"
                    autoComplete="cc-number"
                    maxLength={23}
                    value={cardNumber}
                    onChange={(event) => setCardNumber(formatCardNumberInput(event.target.value))}
                  />
                  {cardBrand ? <span className="vp-card-brand">{cardBrand}</span> : null}
                </div>
              </label>
              <label className="vp-field">
                <span>{tr('payment.cardholderName')}</span>
                <input placeholder={tr('payment.cardholderPlaceholder')} autoComplete="cc-name" />
              </label>
              <div className="vp-field-row">
                <label className="vp-field">
                  <span>{tr('payment.expiry')}</span>
                  <input
                    inputMode="numeric"
                    placeholder="MM/AA"
                    autoComplete="cc-exp"
                    maxLength={5}
                    value={cardExpiry}
                    onChange={(event) => setCardExpiry(formatCardExpiryInput(event.target.value))}
                  />
                </label>
                <label className="vp-field">
                  <span>{tr('payment.cvv')}</span>
                  <input inputMode="numeric" placeholder="000" autoComplete="cc-csc" />
                </label>
              </div>
            </div>
          )}

          {manualPaymentVisible && !walletInteractionActive && activeMethod === 'boleto' && (
            <div className="vp-panel vp-panel--info">
              <BarcodeIcon width={18} height={18} />
              <p>{tr('payment.boletoHelp')}</p>
            </div>
          )}

          {isSubscription && selectedPlan && (
            <p className="vp-sub-terms">
              {!isFreeTrial && (
                <span className="vp-sub-charge-summary">
                  {tr('summary.totalToday')}: {totalLabel}
                </span>
              )}
              {isFreeTrial
                ? tr('subscription.freeTerms', { days: selectedPlan.trialDays })
                : selectedPlan.trialDays > 0 && selectedPlan.trialPrice
                  ? tr('subscription.afterPaidTrialTerms', {
                      days: selectedPlan.trialDays,
                    })
                  : tr('subscription.renewalTerms')}
              <strong>
                {recurringSummaryLabel ?? recurringPlanLabel}
              </strong>
              {tr('subscription.cancelTerms')}
            </p>
          )}
          {(manualPaymentVisible || walletInteractionActive) && (
            <button type="submit" className={ctaClass} disabled={!canPay}>
              {walletInteractionActive ? (
                <>{tr('checkout.finishWallet', { wallet: activeWalletLabel })}</>
              ) : phase === 'processing' ? (
                <>
                  <span className="vp-spinner vp-spinner--sm" aria-hidden="true" />
                  {tr('common.processing')}
                </>
              ) : (
                <>
                  <LockIcon width={18} height={18} />
                  {ctaText}
                </>
              )}
            </button>
          )}

          <div className="vp-trust">
            {hasGuarantee && (
              <span>
                <ShieldIcon width={15} height={15} /> {tr('checkout.guarantee', { days: refundDays })}
              </span>
            )}
            {showSecurity && (
              <span>
                <LockIcon width={15} height={15} /> {tr('checkout.dataProtected')}
              </span>
            )}
            {showSecurity && (
              <span>
                <CheckIcon width={15} height={15} /> {tr('checkout.cardNotStored')}
              </span>
            )}
          </div>

          <p className="vp-footer">
            {tr('payment.processedBy')}
          </p>
        </form>

        <div
          className={`vp-col-summary vp-col-summary--side ${visibleBumps.length <= 1 ? 'is-sticky' : ''}`}
        >
          {summary}
          {showBumpsInSidebar && visibleBumps.length > 0 && (
            <section className="vp-summary-bump-section" aria-label={tr('checkout.completeOrder')}>
              <h2>{tr('checkout.completeOrder')}</h2>
              <OrderBumpList
                items={visibleBumps}
                selectedIds={selectedBumpIds}
                onToggle={toggleBump}
                placement="sidebar"
                disabled={formLocked}
              />
            </section>
          )}
        </div>
      </main>

      {(manualPaymentVisible || walletInteractionActive) && (
        <div className="vp-stickybar">
          <div className="vp-stickybar-info">
            <span className="vp-faint vp-small">{tr('common.total')}</span>
            <strong className="vp-num">{totalLabel}</strong>
          </div>
          <button type="submit" form="vp-checkout-form" className={ctaClass} disabled={!canPay}>
            {walletInteractionActive
              ? tr('checkout.finishWallet', { wallet: activeWalletLabel })
              : phase === 'processing'
              ? tr('common.processing')
              : isFreeTrial
                ? tr('checkout.startFree')
                : isSubscription
                  ? tr('checkout.subscribeNow')
                  : isPix
                    ? tr('checkout.payPix')
                  : tr('checkout.payNow')}
          </button>
        </div>
      )}
    </div>
  );
}
